(function installCcmLivePreviewV3() {
  if (customElements.get('ccm-live-preview-v3')) return;

  const APPEARANCE_VERSION = '20260905-neutral-studio-v1';
  const HANDOFF_KEY = 'dccom-live-preview-v3-handoff-v1';
  const DRAG_HINT_KEY = 'dccom-live-preview-v3-drag-dismissed-v1';
  const REAR_HINT_KEY = 'dccom-live-preview-v3-rear-shown-v1';
  const VIEWER_COMMAND_SCHEMA = 'ccm-storefront-viewer-command/v1';
  const STOREFRONT_RELEASE = 'dccom-live-preview-v3-20260816-clean-front-pose';
  const STOREFRONT_RELEASE_QUERY_KEY = 'v3_storefront_release';
  const HANDOFF_MAX_AGE_MS = 2 * 60 * 60 * 1000;
  const MAX_RENDERER_AUTO_RETRIES = 1;
  const RENDERER_AUTO_RETRY_DELAY_MS = 450;
  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const MAX_IMAGE_PIXELS = 40000000;
  const ACCEPTED_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
  ]);

  // Discount Crystals black-base SKUs in Canopy use the LBBR**01 family.
  // The renderer/app catalog canonicalizes those production SKUs to its **02 assets.
  function blackBaseSkuForCrystal(rawSku, fallbackOrientation = 'tower') {
    const sku = String(rawSku || '').toUpperCase().trim();
    const crystal = sku.match(/(SCRT|SCHR|SCSQ|SCPR)(\d{2})\d{2}/);
    if (!crystal) return 'none';

    if (crystal[1] !== 'SCRT') return 'LBBR0701';

    const size = Number(crystal[2]);
    const isBrick = /-B(?:-|$)/.test(sku)
      || (!/-T(?:-|$)/.test(sku) && String(fallbackOrientation).toLowerCase() === 'brick');
    if (isBrick) {
      if (size <= 6) return 'LBBR0701';
      if (size <= 10) return 'LBBR1101';
      if (size <= 15) return 'LBBR1601';
      return 'LBBR2101';
    }

    if (size <= 10) return 'LBBR0701';
    if (size <= 12) return 'LBBR1101';
    if (size <= 18) return 'LBBR1601';
    return 'LBBR2101';
  }

  function createUuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const random = window.crypto?.getRandomValues
        ? window.crypto.getRandomValues(new Uint8Array(1))[0] % 16
        : Math.floor(Math.random() * 16);
      const value = character === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function safeJsonParse(value) {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function wait(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  function currentReleasePath(path) {
    if (!path) return path;
    const url = new URL(path, window.location.origin);
    url.searchParams.set(STOREFRONT_RELEASE_QUERY_KEY, STOREFRONT_RELEASE);
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : url.href;
  }

  class CcmLivePreviewV3 extends HTMLElement {
    constructor() {
      super();
      this.v3Active = false;
      this.file = null;
      this.objectUrl = '';
      this.sourceImageRequestId = 0;
      this.legacyPhoto = null;
      this.legacyUploadPromise = null;
      this.createPromise = null;
      this.createAbortController = null;
      this.pollTimer = null;
      this.pollStartedAt = 0;
      this.pollInFlight = false;
      this.configureTimer = null;
      this.configureInFlight = false;
      this.configureQueued = false;
      this.failureInProgress = false;
      this.lastFailure = null;
      this.backgroundRegenerationPromise = null;
      this.browserDiagnosticsCache = null;
      this.analyticsEmitted = new Set();
      this.sessionId = '';
      this.previewToken = '';
      this.statusPath = '';
      this.configurePath = '';
      this.cartAssociationPath = '';
      this.rendererPath = '';
      this.posterPath = '';
      this.clientEventPath = '';
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      this.rendererSlowTimer = null;
      this.guidanceTimer = null;
      this.posterTimer = null;
      this.currentRevision = -1;
      this.inferenceStatus = '';
      this.statusValue = '';
      this.lastSentSignature = '';
      this.loadedRendererKey = '';
      this.rendererAutoRetryKey = '';
      this.rendererAutoRetryAttempts = 0;
      this.rendererAutoRetryInFlight = false;
      this.rendererFrame = null;
      this.pendingRendererFrame = null;
      this.expectedRendererOrigin = '';
      this.pendingRendererOrigin = '';
      this.currentStageKey = 'intake';
      this.currentStageIndex = 1;
      this.currentStageLabel = '';
      this.generationKind = 'initial';
      this.lastWorkingSignature = '';
      this.lastPreviewStateSignature = '';
      this.pendingLedGuidance = false;
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterUploadingRevision = -1;
      this.posterRetryRevision = -1;
      this.allowNextSubmit = false;
      this.submissionInFlight = false;
      this.associatedCartToken = '';
      this.cartEditContext = null;
      this.restoringCartEdit = false;
      this.unsubscribeVariant = null;
      this.textTimer = null;
      this.handleWindowMessage = this.handleWindowMessage.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentInput = this.handleDocumentInput.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleHomepageNavigation = this.handleHomepageNavigation.bind(this);
      this.handlePageHide = this.handlePageHide.bind(this);
    }

    connectedCallback() {
      if (!this.rolloutEnabled()) return;

      this.v3Active = true;
      this.hidden = false;
      this.fileInput = this.querySelector('[data-v3-file-input]');
      this.sample = this.querySelector('[data-v3-sample]');
      this.invitation = this.querySelector('[data-v3-invitation]');
      this.working = this.querySelector('[data-v3-working]');
      this.sourceImage = this.querySelector('[data-v3-source-image]');
      this.overlayHeading = this.querySelector('[data-v3-overlay-heading]');
      this.overlayBody = this.querySelector('[data-v3-overlay-body]');
      this.progress = this.querySelector('[data-v3-progress]');
      this.progressTime = this.querySelector('[data-v3-progress-time]');
      this.rendererHost = this.querySelector('[data-v3-renderer]');
      this.guidance = this.querySelector('[data-v3-guidance]');
      this.guidanceHeading = this.querySelector('[data-v3-guidance-heading]');
      this.guidanceBody = this.querySelector('[data-v3-guidance-body]');
      this.statusHeading = this.querySelector('[data-v3-status-heading]');
      this.statusBody = this.querySelector('[data-v3-status-body]');
      this.changePhotoButton = this.querySelector('[data-v3-change-photo]');
      this.retryButton = this.querySelector('[data-v3-retry]');
      this.keepBackgroundInput = this.querySelector('[data-v3-keep-background]');
      this.cartEditBanner = this.querySelector('[data-v3-cart-edit-banner]');
      this.catalogViewer = this.querySelector('ccm-native-crystal-viewer');
      this.productForm = this.dataset.productFormId
        ? document.getElementById(this.dataset.productFormId)
        : null;
      this.desiredSelection = this.readSelection();
      this.stageLabels = {
        intake: this.dataset.stageIntake,
        background_removal: this.dataset.stageBackgroundRemoval,
        depth_estimation: this.dataset.stageDepthEstimation,
        engraving_field: this.dataset.stageEngravingField,
        field_packaging: this.dataset.stageFieldPackaging,
        crystal_compilation: this.dataset.stageCrystalCompilation,
      };

      this.activateSurface();
      this.bindControls();
      this.showInitial();

      if (this.dataset.surface === 'pdp') {
        this.restorePdpState().catch(error => this.showError(error.message));
        window.CCMOpenProductPreview = () => this.focusViewer();
      }

      this.emitAnalytics('live_preview_v3_opened');
    }

    async restorePdpState() {
      // DC's form, SKU properties and pricing are initialized at DOMContentLoaded.
      // Hydrating earlier lets those listeners overwrite the restored selection.
      if (this.closest('.dc-pdp') && this.productForm && !this.productForm.dataset.dcOptionsReady) {
        await new Promise(resolve => this.productForm.addEventListener('dc:pdp:options-ready', resolve, { once: true }));
      }
      if (!this.isConnected) return;
      const editSessionId = this.cartEditSessionId();
      if (editSessionId) return this.restoreCartEdit(editSessionId);
      this.restoreHandoff();
      await this.restoreNativeHandoff();
      await this.restoreHistoricalPhoto();
    }

    disconnectedCallback() {
      window.removeEventListener('message', this.handleWindowMessage);
      window.removeEventListener('pagehide', this.handlePageHide);
      document.removeEventListener('click', this.handleDocumentClick, true);
      document.removeEventListener('input', this.handleDocumentInput, true);
      document.removeEventListener('change', this.handleDocumentInput, true);
      this.productForm?.removeEventListener('submit', this.handleFormSubmit, true);
      const productFormHost = this.productForm?.closest('product-form');
      if (productFormHost?.cartEditCoordinator === this) productFormHost.cartEditCoordinator = null;
      if (productFormHost?.livePreviewV3Coordinator === this) productFormHost.livePreviewV3Coordinator = null;
      this.homeProductLink?.removeEventListener('click', this.handleHomepageNavigation, true);
      this.unsubscribeVariant?.();
      this.stopPolling();
      window.clearTimeout(this.configureTimer);
      window.clearTimeout(this.textTimer);
      window.clearTimeout(this.rendererSlowTimer);
      window.clearTimeout(this.guidanceTimer);
      window.clearTimeout(this.posterTimer);
      this.createAbortController?.abort();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    }

    get isActive() {
      return this.v3Active;
    }

    rolloutEnabled() {
      const mode = String(this.dataset.mode || 'off').toLowerCase();
      if (mode === 'on') return true;
      return mode === 'internal' && new URLSearchParams(window.location.search).get('preview_v3') === '1';
    }

    activateSurface() {
      if (this.dataset.surface === 'homepage') {
        this.closest('.preview-crystal')?.classList.add('is-v3-active');
      } else {
        const mediaWrapper = this.closest('.product__media-wrapper');
        mediaWrapper?.classList.add('is-v3-active');
        mediaWrapper?.closest('.product')?.classList.add('is-v3-active');
        this.closest('product-info')?.querySelectorAll('.orientation-link').forEach((link) => {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('preview_v3', '1');
          const currentUrl = new URL(window.location.href);
          [
            'preview_theme_id',
            'view',
            'edit_preview_session',
            'edit_cart_group',
            'edit_cart_key',
            'edit_focus',
          ].forEach((parameter) => {
            const value = currentUrl.searchParams.get(parameter);
            if (value) url.searchParams.set(parameter, value);
          });
          link.href = url.pathname + url.search;
        });
      }
    }

    bindControls() {
      this.querySelector('[data-media-home-upload]')?.addEventListener('click', event => this.handleHomepageUpload(event));
      this.querySelector('[data-v3-upload]')?.addEventListener('click', () => this.openFilePicker());
      this.querySelector('[data-v3-dismiss]')?.addEventListener('click', () => this.dismissInvitation());
      this.changePhotoButton?.addEventListener('click', () => this.openFilePicker());
      this.retryButton?.addEventListener('click', () => {
        if (this.file) this.startPreview(this.file);
        else this.openFilePicker();
      });
      this.fileInput?.addEventListener('change', () => {
        const file = this.fileInput.files?.[0];
        if (file) this.handleFile(file);
      });
      if (this.dataset.surface === 'homepage') {
        this.keepBackgroundInput?.addEventListener('change', (event) => {
          this.handleBackgroundChoice(event.currentTarget.checked);
        });
      }

      window.addEventListener('message', this.handleWindowMessage);
      window.addEventListener('pagehide', this.handlePageHide);
      document.addEventListener('click', this.handleDocumentClick, true);
      document.addEventListener('input', this.handleDocumentInput, true);
      document.addEventListener('change', this.handleDocumentInput, true);
      this.productForm?.addEventListener('submit', this.handleFormSubmit, true);
      const productFormHost = this.productForm?.closest('product-form');
      if (productFormHost) productFormHost.livePreviewV3Coordinator = this;
      if (this.dataset.surface === 'homepage') {
        this.homeProductLink = this.closest('.theme-preview-popup')?.querySelector('[data-target="product-url"]');
        this.homeProductLink?.addEventListener('click', this.handleHomepageNavigation, true);
      }

      if (
        this.dataset.surface === 'pdp'
        && typeof subscribe === 'function'
        && typeof PUB_SUB_EVENTS !== 'undefined'
        && PUB_SUB_EVENTS.variantChange
      ) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const section = this.closest('product-info');
          if (section?.dataset.section && data?.sectionId && section.dataset.section !== data.sectionId) return;
          if (data?.variant?.sku) {
            const productSection = this.closest('[id^="MainProduct-"]') || document;
            const baseControl = productSection.querySelector(
              'input[name="base"][data-base-sku]:checked, variant-selects-extended input[data-base-sku]:checked',
            );
            const baseSku = baseControl?.dataset.baseSku === 'auto-black'
              ? blackBaseSkuForCrystal(data.variant.sku, this.dataset.defaultOrientation)
              : baseControl?.dataset.baseSku || this.desiredSelection?.base_sku || 'none';
            this.updateSelection({ crystal_sku: data.variant.sku, base_sku: baseSku });
          }
          if (this.cartEditContext) window.setTimeout(() => this.activateCartEditMode(), 0);
        });
      }
    }

    handleDocumentClick(event) {
      if (!this.v3Active || this.dataset.surface !== 'pdp') return;

      const orientationLink = event.target.closest('.orientation-link');
      if (orientationLink) {
        const url = new URL(orientationLink.href, window.location.origin);
        url.searchParams.set('preview_v3', '1');
        const currentUrl = new URL(window.location.href);
        [
          'preview_theme_id',
          'view',
          'edit_preview_session',
          'edit_cart_group',
          'edit_cart_key',
          'edit_focus',
        ].forEach((parameter) => {
          const value = currentUrl.searchParams.get(parameter);
          if (value) url.searchParams.set(parameter, value);
        });
        orientationLink.href = url.pathname + url.search;
        this.persistHandoff();
        return;
      }

      const button = event.target.closest('#product-file-picker, .product-upload-photo .theme-button-secondary');
      if (!button) return;
      const uploadBlock = button.closest('.product-upload-photo');
      if (
        uploadBlock?.dataset.productFormId
        && this.dataset.productFormId
        && uploadBlock.dataset.productFormId !== this.dataset.productFormId
      ) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.openFilePicker();
    }

    handleDocumentInput(event) {
      if (!this.v3Active || this.dataset.surface !== 'pdp' || this.restoringCartEdit) return;
      const target = event.target;
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
      const formId = target.getAttribute('form');
      if (formId && this.dataset.productFormId && formId !== this.dataset.productFormId) return;

      if (target.matches('input[name="base"][data-base-sku], variant-selects-extended input[data-base-sku]')) {
        const baseSku = target.dataset.baseSku === 'auto-black'
          ? blackBaseSkuForCrystal(this.desiredSelection?.crystal_sku, this.dataset.defaultOrientation)
          : target.dataset.baseSku || 'none';
        this.updateSelection({ base_sku: baseSku });
        return;
      }

      if (target.matches('#keep_background, [data-v3-keep-background]')) {
        this.handleBackgroundChoice(target.checked);
        return;
      }

      if (
        target.name === 'properties[Engrave Text]'
        || target.name === 'properties[Engrave text]'
        || target.matches('.engrave-text__input input[type="text"]')
      ) {
        window.clearTimeout(this.textTimer);
        this.textTimer = window.setTimeout(() => {
          this.updateSelection({ engraving_text: this.engravingTextValue() });
        }, 500);
        return;
      }

      if (
        target.name === 'properties[Font]'
        || target.matches('.engrave-text__styles input[name="font_style"]')
      ) {
        this.updateSelection({ engraving_font: this.engravingFontValue() });
        return;
      }

      if (target.name === 'properties[Crystal SKU]') {
        const crystalSku = target.value || this.dataset.initialSku || '';
        const productSection = this.closest('[id^="MainProduct-"]') || document;
        const baseControl = productSection.querySelector(
          'input[name="base"][data-base-sku]:checked, variant-selects-extended input[data-base-sku]:checked',
        );
        const baseSku = baseControl?.dataset.baseSku === 'auto-black'
          ? blackBaseSkuForCrystal(crystalSku, this.dataset.defaultOrientation)
          : baseControl?.dataset.baseSku || this.desiredSelection?.base_sku || 'none';
        this.updateSelection({ crystal_sku: crystalSku, base_sku: baseSku });
      }
    }

    controlValue(name, fallback = '') {
      const formId = this.dataset.productFormId;
      const form = formId ? document.getElementById(formId) : null;
      const associatedControl = formId
        ? document.querySelector(`[form="${CSS.escape(formId)}"][name="${CSS.escape(name)}"]`)
        : null;
      if (associatedControl) return associatedControl.value;
      const formControl = form?.querySelector(`[name="${CSS.escape(name)}"]`);
      return formControl ? formControl.value : fallback;
    }

    engravingTextValue() {
      const scope = this.closest('product-info') || document;
      const visibleControl = scope.querySelector('.engrave-text__input input[type="text"]');
      if (visibleControl) return visibleControl.value || '';

      const legacyValue = this.controlValue(
        'properties[Engrave Text]',
        this.controlValue('properties[Engrave text]', ''),
      );
      return String(legacyValue || '').replace(/\s+\((?:Georgia|Monotype Corsiva)\)$/, '');
    }

    engravingFontValue() {
      const scope = this.closest('product-info') || document;
      const visibleControl = scope.querySelector('.engrave-text__styles input[name="font_style"]:checked');
      if (visibleControl) return visibleControl.value || 'Georgia';

      const explicitFont = this.controlValue('properties[Font]', '');
      if (explicitFont) return explicitFont;
      const legacyValue = this.controlValue(
        'properties[Engrave Text]',
        this.controlValue('properties[Engrave text]', ''),
      );
      return String(legacyValue || '').match(/\((Georgia|Monotype Corsiva)\)$/)?.[1] || 'Georgia';
    }

    readSelection() {
      const scope = this.closest('product-info') || document;
      const selectedBase = scope.querySelector(
        'input[name="base"][data-base-sku]:checked, variant-selects-extended input[data-base-sku]:checked'
      );
      const selectedHomepageCrystal = this.dataset.surface === 'homepage'
        ? this.closest('.theme-preview-popup')
          ?.querySelector('.crystal-type input[type="radio"]:checked')
          ?.dataset.productSku
        : '';
      const selectedCrystal = scope.querySelector('.product-option__size input[data-crystal-sku]:checked');
      const crystalSku = selectedCrystal?.dataset.crystalSku || this.controlValue(
          'properties[Crystal SKU]',
          selectedHomepageCrystal || this.desiredSelection?.crystal_sku || this.dataset.initialSku || '',
        );
      const selectedBaseSku = selectedBase?.dataset.baseSku
        || this.desiredSelection?.base_sku
        || this.dataset.initialBaseSku
        || 'none';
      return {
        crystal_sku: crystalSku,
        orientation: this.dataset.defaultOrientation || 'tower',
        base_sku: selectedBaseSku === 'auto-black'
          ? blackBaseSkuForCrystal(crystalSku, this.dataset.defaultOrientation)
          : selectedBaseSku,
        engraving_text: this.engravingTextValue(),
        engraving_font: this.engravingFontValue(),
      };
    }

    syncSelectionFromControls() {
      const selection = this.readSelection();
      this.desiredSelection = { ...this.desiredSelection, ...selection };
      return this.desiredSelection;
    }

    keepBackgroundControl() {
      if (this.dataset.surface === 'pdp') {
        const productSection = this.closest('[id^="MainProduct-"]') || document;
        return productSection.querySelector('#keep_background') || this.keepBackgroundInput;
      }
      return this.keepBackgroundInput;
    }

    syncBackgroundChoice(checked) {
      const productSection = this.closest('[id^="MainProduct-"]') || document;
      productSection.querySelectorAll('#keep_background, [data-v3-keep-background]').forEach((input) => {
        input.checked = Boolean(checked);
      });
    }

    async restoredPreviewFile() {
      const sourceUrl = String(this.legacyPhoto?.url || '').trim();
      if (!sourceUrl) return null;
      if (!this.nativeAsset && window.NativePhotoCompat?.legacyUrl(sourceUrl)) {
        const adopted = await window.NativePhotoCompat.adopt(sourceUrl);
        if (this.legacyPhoto?.url !== sourceUrl) throw new DOMException('Photo changed', 'AbortError');
        this.nativeAsset = adopted; this.nativeMedia = true;
        this.legacyPhoto = {...this.legacyPhoto, ...adopted};
        this.legacyUploadPromise = Promise.resolve(adopted);
        return {restored:true};
      }
      if (this.nativeMedia && this.nativeAsset) return {restored:true};

      const response = await fetch(sourceUrl, {
        credentials: 'omit',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(this.dataset.copyErrorBody);
      const blob = await response.blob();
      if (!blob.size) throw new Error(this.dataset.copyInvalidFile);
      const sourceName = String(this.legacyPhoto?.filename || 'customer-photo.jpg');
      const hasExtension = /\.[a-z0-9]{2,5}$/i.test(sourceName);
      const extension = blob.type === 'image/png'
        ? 'png'
        : blob.type === 'image/webp' ? 'webp' : 'jpg';
      return new File(
        [blob],
        hasExtension ? sourceName : `${sourceName}.${extension}`,
        { type: blob.type || 'image/jpeg' },
      );
    }

    handleBackgroundChoice(checked = this.keepBackgroundControl()?.checked) {
      this.syncBackgroundChoice(Boolean(checked));
      this.persistHandoff();
      if (this.backgroundRegenerationPromise) return this.backgroundRegenerationPromise;

      this.backgroundRegenerationPromise = (async () => {
        let sourceFile = this.file instanceof File ? this.file : null;
        if (!sourceFile) sourceFile = await this.restoredPreviewFile();
        if (!sourceFile) {
          this.openFilePicker();
          return null;
        }
        this.file = sourceFile;
        this.generationKind = 'initial';
        this.currentStageKey = 'intake';
        this.currentStageIndex = 1;
        this.currentStageLabel = this.stageLabels.intake || this.dataset.copyProcessingHeading;
        this.showWorking(
          this.currentStageLabel,
          this.dataset.copyProcessingBody,
          this.currentStageIndex,
          this.generationKind,
        );
        return this.startPreview(sourceFile);
      })()
        .catch((error) => {
          this.showError(error?.message || this.dataset.copyErrorBody);
          return null;
        })
        .finally(() => {
          this.backgroundRegenerationPromise = null;
        });

      return this.backgroundRegenerationPromise;
    }

    updateSelection(nextSelection) {
      const previousSignature = this.selectionSignature(this.desiredSelection);
      const previousBase = this.desiredSelection?.base_sku || 'none';
      this.desiredSelection = { ...this.desiredSelection, ...nextSelection };
      const nextSignature = this.selectionSignature(this.desiredSelection);
      if (previousSignature === nextSignature) return;

      if (nextSelection.crystal_sku && this.catalogViewer?.setSku) {
        this.catalogViewer.setSku(nextSelection.crystal_sku);
      }
      if (nextSelection.base_sku && this.catalogViewer) {
        this.catalogViewer.setAttribute('base-mode', nextSelection.base_sku);
      }
      if (
        nextSelection.base_sku
        && nextSelection.base_sku !== 'none'
        && nextSelection.base_sku !== previousBase
      ) {
        this.pendingLedGuidance = true;
      }
      if (this.sessionId) {
        this.emitAnalytics('live_preview_v3_product_changed');
        this.scheduleConfigure();
      }
      this.persistHandoff();
    }

    dismissInvitation() {
      if (!this.invitation) return;
      this.invitation.classList.add('is-dismissed');
      this.changePhotoButton.textContent = this.dataset.copyUpload;
      this.changePhotoButton.hidden = false;
    }

    handlePageHide() {
      this.persistHandoff();
    }

    setSelection(nextSelection) {
      this.updateSelection(nextSelection);
    }

    selectionSignature(selection) {
      if (!selection) return '';
      return JSON.stringify([
        selection.crystal_sku || '',
        selection.orientation || '',
        selection.base_sku || 'none',
        selection.engraving_text || '',
        selection.engraving_font || 'Georgia',
      ]);
    }

    mountExternalUploader(container) {
      if (!this.v3Active || !container) return;
      const button = document.createElement('button');
      const heading = document.createElement('strong');
      const body = document.createElement('span');
      button.type = 'button';
      button.className = 'ccm-live-preview-v3__external-upload';
      heading.textContent = this.dataset.copyUpload;
      body.textContent = this.dataset.copyInitialBody;
      button.append(heading, body);
      button.addEventListener('click', () => this.openFilePicker());
      container.replaceChildren(button);
      container.addEventListener('dragover', (event) => {
        event.preventDefault();
        container.classList.add('is-dragging');
      });
      container.addEventListener('dragleave', () => container.classList.remove('is-dragging'));
      container.addEventListener('drop', (event) => {
        event.preventDefault();
        container.classList.remove('is-dragging');
        const file = event.dataTransfer?.files?.[0];
        if (file) this.handleFile(file);
      });
    }

    openFilePicker() {
      if (!this.v3Active) {
        this.openLegacyPicker();
        return;
      }
      this.openV3Picker();
    }

    resetAndOpen() {
      if (!this.v3Active) {
        this.openLegacyPicker();
        return;
      }
      this.stopPolling();
      this.createAbortController?.abort();
      this.sessionId = '';
      this.previewToken = '';
      this.statusPath = '';
      this.configurePath = '';
      this.cartAssociationPath = '';
      this.rendererPath = '';
      this.posterPath = '';
      this.clientEventPath = '';
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      window.clearTimeout(this.rendererSlowTimer);
      window.clearTimeout(this.guidanceTimer);
      window.clearTimeout(this.posterTimer);
      this.currentRevision = -1;
      this.lastSentSignature = '';
      this.loadedRendererKey = '';
      this.rendererAutoRetryKey = '';
      this.rendererAutoRetryAttempts = 0;
      this.rendererAutoRetryInFlight = false;
      this.rendererFrame = null;
      this.pendingRendererFrame = null;
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterUploadingRevision = -1;
      this.posterRetryRevision = -1;
      this.lastPreviewStateSignature = '';
      this.associatedCartToken = '';
      this.rendererHost?.replaceChildren();
      this.clearLineProperties();
      this.file = null;
      this.mediaSelectionId = (this.mediaSelectionId || 0) + 1;
      this.mediaUploadController?.abort();
      this.nativeAsset = null;
      this.nativeMedia = false;
      window.CustomerMedia?.clearHandoff();
      this.legacyPhoto = null;
      this.legacyUploadPromise = null;
      this.lastFailure = null;
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = '';
      try {
        sessionStorage.removeItem(HANDOFF_KEY);
      } catch (_error) {
        // Storage can be unavailable in strict privacy modes.
      }
      this.showInitial();
      this.openFilePicker();
    }

    createDeferred() {
      let resolve;
      let reject;
      const promise = new Promise((resolvePromise, rejectPromise) => {
        resolve = resolvePromise;
        reject = rejectPromise;
      });
      return { promise, resolve, reject };
    }

    async openV3Picker() {
      const generation = this.photoGeneration || 0;
      try {
        const choice = await window.NativePhotoCompat.choose();
        if (!choice || generation !== (this.photoGeneration || 0)) return;
        this.nativeChoice = choice;
        await this.handleFile(choice.file, { nativeAsset: choice.asset });
      } catch (error) { this.showError(error.message || this.dataset.copyErrorBody); }
    }

    async openLegacyPicker() {
      const generation = this.photoGeneration || 0;
      try {
        const choice = await window.NativePhotoCompat.choose();
        if (!choice || generation !== (this.photoGeneration || 0)) return;
        const data = window.NativePhotoCompat.metadata(choice);
        this.nativeAsset = choice.asset;
        this.setLineProperty('Photo', data.url, false);
        this.setLineProperty('_media_asset_id', choice.asset.asset_id, true);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.filestackFileChoiced, {data, formId:this.dataset.productFormId});
        }
      } catch (error) { this.showError(error.message || this.dataset.copyErrorBody); }
    }

    async handleFile(file, options = {}) {
      const selectionId = (this.mediaSelectionId || 0) + 1;
      this.mediaSelectionId = selectionId;
      const validationMessage = await this.validateFile(file);
      if (selectionId !== this.mediaSelectionId) return false;
      if (validationMessage) {
        this.showError(validationMessage);
        return false;
      }

      let useNative;
      try {
        await window.NativePhotoCompat.requireNative();
        useNative = true;
      } catch (error) {
        this.showError(error.message);
        return false;
      }
      if (selectionId !== this.mediaSelectionId) return false;
      this.file = file;
      this.nativeMedia = useNative;
      this.nativeAsset = null;
      this.nativePreviewAssociated = false;
      this.mediaUploadController?.abort();
      this.mediaUploadController = new AbortController();
      if (!options.preserveReorderSource) this.markAccountReorderPhotoChanged();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = URL.createObjectURL(file);
      this.clearLineProperties();
      this.legacyPhoto = null;
      this.associatedCartToken = '';
      this.legacyUploadPromise = this.nativeMedia
        ? Promise.resolve(options.nativeAsset || window.CustomerMedia.upload(file, { signal: this.mediaUploadController.signal }))
          .then(asset => {
            if (selectionId !== this.mediaSelectionId) throw new DOMException('Photo changed', 'AbortError');
            this.nativeAsset = asset;
            window.CustomerMedia.saveHandoff(asset, this.desiredSelection);
            return this.rememberLegacyPhoto(asset, file, options);
          })
        : this.uploadLegacyPhoto(file, options);
      this.generationKind = 'initial';
      this.currentStageKey = 'intake';
      this.currentStageIndex = 1;
      this.currentStageLabel = this.stageLabels.intake || this.dataset.copyProcessingHeading;
      this.showWorking(
        this.currentStageLabel,
        this.dataset.copyProcessingBody,
        this.currentStageIndex,
        this.generationKind,
      );
      this.emitPreviewState('creating');
      this.dispatchEvent(new CustomEvent('ccm:live-preview-v3:file-selected', {
        detail: {
          thumbnailUrl: this.objectUrl,
          accountReorderHydration: options.preserveReorderSource === true,
        },
        bubbles: true,
      }));
      this.startPreview(file).catch(() => {});
      return true;
    }

    async validateFile(file) {
      const type = String(file.type || '').toLowerCase();
      const extension = String(file.name || '').split('.').pop()?.toLowerCase();
      const extensionAccepted = extension === 'heic' || extension === 'heif';
      if ((!ACCEPTED_TYPES.has(type) && !extensionAccepted) || !file.size) {
        return this.dataset.copyInvalidFile;
      }
      if (file.size > MAX_FILE_BYTES) return this.dataset.copyFileTooLarge;

      if (type === 'image/heic' || type === 'image/heif' || extensionAccepted) return '';
      if (!window.createImageBitmap) return '';
      try {
        const bitmap = await createImageBitmap(file);
        const pixels = bitmap.width * bitmap.height;
        this.fileMetrics = { width: bitmap.width, height: bitmap.height };
        bitmap.close();
        if (pixels > MAX_IMAGE_PIXELS) return this.dataset.copyFileTooLarge;
      } catch (_error) {
        return this.dataset.copyInvalidFile;
      }
      return '';
    }

    startPreview(file) {
      this.stopPolling();
      this.createAbortController?.abort();
      this.createAbortController = new AbortController();
      this.failureInProgress = false;
      [
        'live_preview_v3_upload_started',
        'live_preview_v3_session_created',
        'live_preview_v3_ready',
        'live_preview_v3_failed',
      ].forEach((eventName) => this.analyticsEmitted.delete(eventName));
      this.sessionId = createUuid();
      this.previewToken = '';
      this.statusPath = '';
      this.configurePath = '';
      this.cartAssociationPath = '';
      this.rendererPath = '';
      this.posterPath = '';
      this.clientEventPath = '';
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      this.currentRevision = -1;
      this.inferenceStatus = '';
      this.statusValue = '';
      this.lastFailure = null;
      this.syncSelectionFromControls();
      this.lastSentSignature = this.selectionSignature(this.desiredSelection);
      this.loadedRendererKey = '';
      this.rendererAutoRetryKey = '';
      this.rendererAutoRetryAttempts = 0;
      this.rendererAutoRetryInFlight = false;
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterUploadingRevision = -1;
      this.posterRetryRevision = -1;
      this.pollStartedAt = Date.now();
      this.emitAnalytics('live_preview_v3_upload_started');

      const body = new FormData();
      body.append('client_session_id', this.sessionId);
      body.append('crystal_sku', this.desiredSelection.crystal_sku);
      body.append('orientation', this.desiredSelection.orientation || 'tower');
      body.append('base_sku', this.desiredSelection.base_sku || 'none');
      body.append('remove_background', this.keepBackgroundControl()?.checked ? '0' : '1');
      body.append('engraving_text', this.desiredSelection.engraving_text || '');
      body.append('engraving_font', this.desiredSelection.engraving_font || 'Georgia');

      const nativeMedia = this.nativeMedia;
      const createSignal = this.createAbortController.signal;
      this.createPromise = Promise.resolve(nativeMedia ? this.legacyUploadPromise : null).then(() => {
        createSignal.throwIfAborted();
        if (nativeMedia) {
          if (!this.nativeAsset) throw new Error(this.dataset.copyCartError);
          body.append('media_asset_id', this.nativeAsset.asset_id);
          body.append('owner_token', window.CustomerMedia.ownerToken());
        } else body.append('image', file, file.name || 'upload');
        return this.requestJson(this.dataset.createPath, {
        method: 'POST',
        body,
        headers: nativeMedia ? window.CustomerMedia.headers() : {},
        signal: createSignal,
        diagnosticBoundary: 'create',
      }); })
        .then((payload) => {
          this.applyResponse(payload);
          this.emitAnalytics('live_preview_v3_session_created');
          this.persistHandoff();
          if (payload.status !== 'ready') this.schedulePoll();
          return payload;
        })
        .catch((error) => {
          if (error.name === 'AbortError') return null;
          this.handlePreviewFailure(error, { phase: 'create' });
          throw error;
        });

      return this.createPromise;
    }

    async requestJson(path, options = {}) {
      const {
        diagnosticBoundary = 'api_request',
        retryTransient = true,
        ...fetchOptions
      } = options;
      const requestHeaders = {
        Accept: 'application/json',
        ...(fetchOptions.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(fetchOptions.headers || {}),
      };
      const attempts = retryTransient ? 2 : 1;
      let lastError = null;
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try {
          return await window.StorefrontRequest.run(async (signal) => {
            const response = await fetch(currentReleasePath(path), {
              credentials: 'same-origin',
              cache: 'no-store',
              ...fetchOptions,
              headers: requestHeaders,
              signal,
            });
            const payload = JSON.parse(await response.text());
            if (!payload || typeof payload !== 'object' || Array.isArray(payload) || Object.keys(payload).length === 0) throw new Error('Invalid preview response');
            if (!response.ok) {
              const error = new Error(payload.message || this.dataset.copyErrorBody);
              error.status = response.status;
              error.code = payload.code || '';
              throw error;
            }
            return payload;
          }, { signal: fetchOptions.signal });
        } catch (error) {
          lastError = error;
          if (error?.name === 'AbortError' || attempt >= attempts || !this.isTransientError(error)) {
            throw error;
          }
          this.emitAnalytics('live_preview_v3_retry_started');
          this.reportClientEvent('retry_started', error, {
            phase: diagnosticBoundary,
            severity: 'warning',
            retryable: true,
          });
          await wait(550);
        }
      }
      throw lastError;
    }

    isTransientError(error) {
      const status = Number(error?.status || 0);
      return error?.name === 'TypeError'
        || status === 408
        || status === 425
        || status === 429
        || status >= 500;
    }

    applyResponse(payload) {
      const revision = Number(payload.compilation_revision);
      if (Number.isFinite(revision) && revision < this.currentRevision) return false;
      if (Number.isFinite(revision)) {
        if (this.currentRevision >= 0 && revision > this.currentRevision) {
          this.invalidatePoster();
        }
        this.currentRevision = revision;
      }

      this.sessionId = payload.session_id || payload.id || this.sessionId;
      this.previewToken = payload.preview_token || this.previewToken;
      this.statusPath = payload.status_path || this.statusPath;
      this.configurePath = payload.configure_path || this.configurePath;
      this.cartAssociationPath = payload.cart_association_path || this.cartAssociationPath;
      // The authenticated backend owns the current renderer release.
      this.rendererPath = payload.renderer_path || this.rendererPath;
      this.posterPath = payload.poster_path || this.posterPath;
      this.clientEventPath = payload.client_event_path || this.clientEventPath;
      this.statusValue = payload.status || this.statusValue;
      this.inferenceStatus = payload.inference_status || this.inferenceStatus;
      if (payload.poster?.appearance_version === APPEARANCE_VERSION) {
        this.applyPoster(payload.poster);
        if (this.posterFullUrl && this.currentRevision >= 0) {
          this.posterUploadRevision = this.currentRevision;
        }
      } else if (payload.poster && this.posterFullUrl) {
        this.invalidatePoster();
      }

      if (payload.status === 'failed') {
        throw new Error(payload.error?.message || payload.message || this.dataset.copyErrorBody);
      }

      if (payload.status === 'ready') {
        if (this.selectionSignature(this.desiredSelection) !== this.lastSentSignature) {
          this.scheduleConfigure(0);
        } else {
          this.loadRenderer();
        }
      } else {
        this.showApiStage(payload.stages || []);
        if (this.inferenceStatus === 'ready') this.scheduleConfigure();
      }

      this.persistHandoff();
      return true;
    }

    showApiStage(stages) {
      const active = stages.find((stage) => stage?.status === 'active');
      const nextPending = stages.find((stage) => stage?.status === 'pending');
      const latestComplete = [...stages].reverse().find((stage) => stage?.status === 'complete');
      const phaseByStage = {
        intake: 1,
        background_removal: 1,
        depth_estimation: 2,
        engraving_field: 2,
        field_packaging: 3,
        crystal_compilation: 4,
      };
      const stageKey = active?.key
        || nextPending?.key
        || latestComplete?.key
        || this.currentStageKey
        || 'intake';
      const reportedPhase = phaseByStage[stageKey] || 1;
      const phase = Math.max(this.currentStageIndex || 1, reportedPhase);
      const heading = this.stageLabels[stageKey] || this.dataset.copyProcessingHeading;
      const elapsed = this.pollStartedAt ? Date.now() - this.pollStartedAt : 0;
      const body = elapsed >= 60000 ? this.dataset.copySlowBody : this.dataset.copyProcessingBody;
      this.currentStageKey = stageKey;
      this.currentStageIndex = phase;
      this.currentStageLabel = heading;
      this.showWorking(heading, body, phase, this.generationKind);
      this.emitPreviewState(elapsed >= 60000 ? 'slow' : 'creating');
    }

    schedulePoll() {
      if (!this.v3Active || !this.statusPath || this.statusValue === 'ready' || this.statusValue === 'failed') return;
      window.clearTimeout(this.pollTimer);
      const elapsed = this.pollStartedAt ? Date.now() - this.pollStartedAt : 0;
      this.pollTimer = window.setTimeout(() => this.poll(), elapsed >= 60000 ? 2000 : 1000);
    }

    async poll() {
      if (this.pollInFlight || !this.statusPath || !this.previewToken) return;
      this.pollInFlight = true;
      try {
        const payload = await this.requestJson(this.statusPath, {
          method: 'POST',
          body: JSON.stringify({ preview_token: this.previewToken }),
          diagnosticBoundary: 'poll',
        });
        this.applyResponse(payload);
        if (payload.status !== 'ready' && payload.status !== 'failed') this.schedulePoll();
      } catch (error) {
        this.handlePreviewFailure(error, { phase: 'poll' });
      } finally {
        this.pollInFlight = false;
      }
    }

    stopPolling() {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.pollInFlight = false;
    }

    scheduleConfigure(delay = 500) {
      if (!this.v3Active || this.failureInProgress || !this.sessionId) return;
      window.clearTimeout(this.configureTimer);
      this.configureTimer = window.setTimeout(() => this.configure(), delay);
    }

    async configure() {
      if (!this.v3Active || this.failureInProgress || !this.configurePath || !this.previewToken) return;
      if (this.configureInFlight) {
        this.configureQueued = true;
        return;
      }
      if (this.inferenceStatus !== 'ready' && this.statusValue !== 'ready' && this.statusValue !== 'compiling') {
        return;
      }

      this.syncSelectionFromControls();
      const signature = this.selectionSignature(this.desiredSelection);
      if (signature === this.lastSentSignature) return;

      this.configureInFlight = true;
      this.configureQueued = false;
      this.generationKind = 'update';
      this.currentStageKey = 'crystal_compilation';
      this.currentStageIndex = 4;
      this.currentStageLabel = this.dataset.copyUpdatingHeading;
      this.showWorking(
        this.dataset.copyUpdatingHeading,
        this.dataset.copyProcessingBody,
        4,
        this.generationKind,
      );
      try {
        const payload = await this.requestJson(this.configurePath, {
          method: 'PATCH',
          diagnosticBoundary: 'configure',
          body: JSON.stringify({
            preview_token: this.previewToken,
            crystal_sku: this.desiredSelection.crystal_sku,
            orientation: this.desiredSelection.orientation || 'tower',
            base_sku: this.desiredSelection.base_sku || 'none',
            engraving_text: this.desiredSelection.engraving_text || '',
            engraving_font: this.desiredSelection.engraving_font || 'Georgia',
          }),
        });
        this.lastSentSignature = signature;
        this.pollStartedAt = Date.now();
        this.applyResponse(payload);
        this.schedulePoll();
      } catch (error) {
        if (error.status === 409 && this.inferenceStatus !== 'ready') {
          this.configureQueued = true;
        } else {
          this.handlePreviewFailure(error, { phase: 'configure' });
        }
      } finally {
        this.configureInFlight = false;
        if (
          this.v3Active
          && !this.failureInProgress
          && (
            this.configureQueued
            || this.selectionSignature(this.desiredSelection) !== this.lastSentSignature
          )
        ) {
          this.scheduleConfigure();
        }
      }
    }

    loadRenderer() {
      if (!this.rendererPath) return;
      const rendererKey = `${this.rendererPath}|${this.currentRevision}`;
      if (rendererKey === this.loadedRendererKey) return;
      if (rendererKey !== this.rendererAutoRetryKey) {
        this.rendererAutoRetryKey = rendererKey;
        this.rendererAutoRetryAttempts = 0;
        this.rendererAutoRetryInFlight = false;
      }
      this.loadedRendererKey = rendererKey;

      const frame = document.createElement('iframe');
      frame.title = this.dataset.copyReadyHeading;
      frame.allow = 'fullscreen';
      frame.loading = 'eager';
      frame.referrerPolicy = 'no-referrer';
      frame.src = this.rendererPath;
      frame.className = 'is-v3-staging';
      frame.addEventListener('error', () => {
        if (frame === this.pendingRendererFrame) {
          frame.remove();
          this.pendingRendererFrame = null;
          this.handlePreviewFailure(
            new Error('The 3D viewer iframe failed to load.'),
            { phase: 'renderer_load', preserveViewer: Boolean(this.rendererFrame) },
          );
        }
      }, { once: true });
      this.pendingRendererFrame?.remove();
      this.pendingRendererFrame = frame;
      this.pendingRendererOrigin = new URL(this.rendererPath, window.location.origin).origin;
      this.rendererHost.append(frame);
      this.rendererHost.hidden = false;
      this.showWorking(
        this.generationKind === 'update'
          ? this.dataset.copyUpdatingHeading
          : (this.currentStageLabel || this.dataset.copyProcessingHeading),
        this.dataset.copyProcessingBody,
        4,
        this.generationKind,
      );
      window.clearTimeout(this.rendererSlowTimer);
      this.rendererSlowTimer = window.setTimeout(() => {
        if (this.dataset.state !== 'ready') {
          this.showWorking(
            this.currentStageLabel || this.dataset.copyProcessingHeading,
            this.dataset.copySlowBody,
            4,
            this.generationKind,
          );
          this.emitPreviewState('slow');
        }
      }, 60000);
    }

    handleWindowMessage(event) {
      const fromPending = (
        this.pendingRendererFrame
        && event.source === this.pendingRendererFrame.contentWindow
        && event.origin === this.pendingRendererOrigin
      );
      const fromCurrent = (
        this.rendererFrame
        && event.source === this.rendererFrame.contentWindow
        && event.origin === this.expectedRendererOrigin
      );
      if (!fromPending && !fromCurrent) return;
      const payload = event.data || {};
      if (payload.type === 'ccm:crystal-viewer:ready') {
        this.activateRenderer(fromPending);
      } else if (payload.type === 'ccm:crystal-viewer:bridge-ready') {
        if (fromPending && this.dataset.state !== 'ready') {
          this.activateRenderer(true);
        } else {
          this.fitRendererSelection();
          this.startViewerIntroduction();
        }
      } else if (payload.type === 'ccm:crystal-viewer:interaction-start') {
        try {
          localStorage.setItem(DRAG_HINT_KEY, '1');
        } catch (_error) {
          // Guidance remains non-blocking when storage is unavailable.
        }
        this.hideGuidance();
      } else if (payload.type === 'ccm:crystal-viewer:rear-reached') {
        let shown = false;
        try {
          shown = localStorage.getItem(REAR_HINT_KEY) === '1';
          if (!shown) localStorage.setItem(REAR_HINT_KEY, '1');
        } catch (_error) {
          shown = false;
        }
        if (!shown) {
          this.showGuidance(this.dataset.copyRearHeading, this.dataset.copyRearBody, 7500);
        }
      } else if (payload.type === 'ccm:crystal-viewer:intro-complete') {
        let dismissed = false;
        try {
          dismissed = localStorage.getItem(DRAG_HINT_KEY) === '1';
        } catch (_error) {
          dismissed = false;
        }
        if (!dismissed) {
          this.showGuidance(this.dataset.copyDragHeading, this.dataset.copyDragBody, 6000);
        }
        this.schedulePosterCapture();
      } else if (payload.type === 'ccm:crystal-viewer:poster-ready') {
        this.uploadPoster(payload).catch(() => {});
      } else if (payload.type === 'ccm:crystal-viewer:poster-error') {
        if (Number(payload.revision) === this.currentRevision) {
          this.emitPosterFailure(
            'renderer',
            Number(payload.revision),
            0,
            payload.message || 'Renderer poster capture failed.',
          );
          this.posterCaptureRevision = -1;
          if (!this.posterRetryRevision || this.posterRetryRevision !== this.currentRevision) {
            this.posterRetryRevision = this.currentRevision;
            this.schedulePosterCapture(1200);
          }
        }
      } else if (payload.type === 'ccm:crystal-viewer:error') {
        const error = new Error(String(payload.message || this.dataset.copyErrorBody));
        error.name = String(payload.errorType || payload.name || 'CrystalViewerError').slice(0, 160);
        error.code = String(payload.code || '').slice(0, 160);
        this.handlePreviewFailure(error, {
          phase: String(payload.phase || 'renderer').slice(0, 64),
          preserveViewer: Boolean(fromPending && this.rendererFrame),
        });
      }
    }

    activateRenderer(fromPending) {
      const readyFrame = fromPending ? this.pendingRendererFrame : this.rendererFrame;
      if (!readyFrame) return;
      window.clearTimeout(this.rendererSlowTimer);
      if (fromPending) {
        const previousFrame = this.rendererFrame;
        this.rendererFrame = readyFrame;
        this.expectedRendererOrigin = this.pendingRendererOrigin;
        this.pendingRendererFrame = null;
        this.pendingRendererOrigin = '';
        readyFrame.classList.remove('is-v3-staging');
        readyFrame.classList.add('is-v3-active');
        if (previousFrame && previousFrame !== readyFrame) {
          previousFrame.classList.remove('is-v3-active');
          previousFrame.classList.add('is-v3-retiring');
          window.setTimeout(() => previousFrame.remove(), 450);
        }
      }
      this.sample.hidden = true;
      this.working.hidden = true;
      this.rendererHost.hidden = false;
      this.dataset.state = 'ready';
      this.statusHeading.textContent = this.dataset.copyReadyHeading;
      this.statusBody.textContent = this.dataset.copyReadyBody;
      this.changePhotoButton.textContent = this.dataset.copyChangePhoto;
      this.changePhotoButton.hidden = false;
      this.retryButton.hidden = true;
      this.emitPreviewState('ready');
      this.emitAnalytics('live_preview_v3_ready');
      if (this.rendererAutoRetryInFlight) {
        this.reportClientEvent('viewer_auto_retry_recovered', null, {
          phase: 'renderer_retry',
          severity: 'info',
          retryable: true,
        });
        this.rendererAutoRetryInFlight = false;
      }
      this.persistHandoff();
      this.fitRendererSelection();
      this.startViewerIntroduction();
      this.revealViewerIfAboveViewport();
      if (this.pendingLedGuidance) {
        this.pendingLedGuidance = false;
        let dragDismissed = false;
        try {
          dragDismissed = localStorage.getItem(DRAG_HINT_KEY) === '1';
        } catch (_error) {
          dragDismissed = false;
        }
        window.setTimeout(() => {
          this.showGuidance(this.dataset.copyLedHeading, this.dataset.copyLedBody, 6500);
        }, dragDismissed ? 900 : 6800);
      }
    }

    fitRendererSelection() {
      this.postViewerCommand('ccm:crystal-viewer:fit-selection', {
        crystalSku: this.desiredSelection?.crystal_sku || '',
        baseSku: this.desiredSelection?.base_sku || 'none',
      });
    }

    revealViewerIfAboveViewport() {
      if (this.dataset.surface !== 'pdp') return;
      window.requestAnimationFrame(() => {
        const bounds = this.getBoundingClientRect();
        if (bounds.top >= -1) return;
        const header = document.querySelector('sticky-header, .header-wrapper');
        const headerHeight = Math.max(0, header?.getBoundingClientRect().height || 0);
        const top = Math.max(0, bounds.top + window.scrollY - headerHeight - 16);
        window.scrollTo({
          top,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    }

    postViewerCommand(type, detail = {}) {
      if (!this.rendererFrame?.contentWindow) return;
      this.rendererFrame.contentWindow.postMessage(
        { schema: VIEWER_COMMAND_SCHEMA, type, ...detail },
        this.expectedRendererOrigin,
      );
    }

    startViewerIntroduction() {
      window.clearTimeout(this.posterTimer);
      let dismissed = false;
      try {
        dismissed = localStorage.getItem(DRAG_HINT_KEY) === '1';
      } catch (_error) {
        dismissed = false;
      }
      if (dismissed) {
        this.schedulePosterCapture(450);
        return;
      }
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.showGuidance(this.dataset.copyDragHeading, this.dataset.copyDragBody, 9000);
        this.schedulePosterCapture(450);
        return;
      }
      this.postViewerCommand('ccm:crystal-viewer:intro-orbit');
      this.posterTimer = window.setTimeout(() => this.schedulePosterCapture(0), 3200);
    }

    showGuidance(heading, body, duration = 7000) {
      if (!this.guidance || !heading) return;
      window.clearTimeout(this.guidanceTimer);
      this.guidanceHeading.textContent = heading;
      this.guidanceBody.textContent = body || '';
      this.guidance.hidden = false;
      this.guidanceTimer = window.setTimeout(() => this.hideGuidance(), duration);
    }

    hideGuidance() {
      window.clearTimeout(this.guidanceTimer);
      if (this.guidance) this.guidance.hidden = true;
    }

    schedulePosterCapture(delay = 500) {
      if (
        !this.rendererFrame
        || !this.posterPath
        || this.currentRevision < 0
        || this.posterCaptureRevision === this.currentRevision
      ) return;
      window.clearTimeout(this.posterTimer);
      this.posterTimer = window.setTimeout(() => {
        this.posterCaptureRevision = this.currentRevision;
        this.postViewerCommand('ccm:crystal-viewer:capture-poster', {
          revision: this.currentRevision,
        });
      }, delay);
    }

    async dataUrlToBlob(dataUrl) {
      const response = await fetch(dataUrl);
      if (!response.ok) throw new Error('Preview image conversion failed.');
      return response.blob();
    }

    async normalizedPosterBlob(dataUrl, width, height) {
      const sourceBlob = await this.dataUrlToBlob(dataUrl);
      let source;
      let sourceWidth;
      let sourceHeight;
      if (window.createImageBitmap) {
        source = await createImageBitmap(sourceBlob);
        sourceWidth = source.width;
        sourceHeight = source.height;
      } else {
        const objectUrl = URL.createObjectURL(sourceBlob);
        try {
          source = await new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = objectUrl;
          });
          sourceWidth = source.naturalWidth;
          sourceHeight = source.naturalHeight;
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      }
      if (!sourceWidth || !sourceHeight) throw new Error('Preview image has invalid dimensions.');

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('Preview image conversion is unavailable.');
      context.fillStyle = '#171a1d';
      context.fillRect(0, 0, width, height);
      const scale = Math.max(width / sourceWidth, height / sourceHeight);
      const drawWidth = sourceWidth * scale;
      const drawHeight = sourceHeight * scale;
      context.drawImage(
        source,
        (width - drawWidth) / 2,
        (height - drawHeight) / 2,
        drawWidth,
        drawHeight,
      );
      source.close?.();

      const encode = (type, quality) => new Promise((resolve) => {
        canvas.toBlob(resolve, type, quality);
      });
      return (await encode('image/webp', 0.86)) || (await encode('image/jpeg', 0.9));
    }

    emitPosterFailure(reason, revision, status = 0, message = '') {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'live_preview_v3_poster_failed',
          live_preview_store: this.dataset.store || 'ccm',
          live_preview_version: 'v3',
          live_preview_session_id: this.sessionId || '',
          live_preview_revision: revision,
          live_preview_failure_reason: reason,
          live_preview_http_status: Number(status) || 0,
        });
      }
      const error = new Error(message || `Preview poster ${reason} failed.`);
      error.status = Number(status) || 0;
      this.reportClientEvent('poster_failed', error, {
        phase: `poster_${reason}`,
        severity: 'warning',
        retryable: true,
      });
    }

    async uploadPoster(payload) {
      const revision = Number(payload.revision);
      if (
        !this.posterPath
        || !this.previewToken
        || !Number.isFinite(revision)
        || revision !== this.currentRevision
        || revision === this.posterUploadRevision
        || revision === this.posterUploadingRevision
        || !payload.posterDataUrl
        || !payload.thumbnailDataUrl
      ) return;
      this.posterUploadingRevision = revision;
      try {
        const [poster, thumbnail] = await Promise.all([
          this.normalizedPosterBlob(payload.posterDataUrl, 960, 720),
          this.normalizedPosterBlob(payload.thumbnailDataUrl, 480, 480),
        ]);
        if (!poster || !thumbnail) throw new Error('Preview image conversion failed.');
        const body = new FormData();
        body.append('preview_token', this.previewToken);
        body.append('revision', String(revision));
        body.append('appearance_version', APPEARANCE_VERSION);
        body.append('poster', poster, `crystal-preview-${revision}.webp`);
        body.append('thumbnail', thumbnail, `crystal-preview-${revision}-square.webp`);
        const response = await this.requestJson(this.posterPath, {
          method: 'POST',
          body,
          diagnosticBoundary: 'poster_upload',
        });
        if (response.poster) {
          this.applyPoster(response.poster);
          this.posterUploadRevision = revision;
        }
        this.persistHandoff();
        this.emitPreviewState('ready');
      } catch (error) {
        this.posterUploadRevision = -1;
        this.posterCaptureRevision = -1;
        this.emitPosterFailure('upload', revision, error.status, error.message);
        if (error.status === 409 && revision !== this.currentRevision) return;
        if (this.posterRetryRevision !== revision) {
          this.posterRetryRevision = revision;
          this.schedulePosterCapture(1200);
        }
      } finally {
        this.posterUploadingRevision = -1;
      }
    }

    invalidatePoster() {
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterUploadingRevision = -1;
      this.posterRetryRevision = -1;
      const form = this.productForm;
      form?.querySelector('[name="properties[_preview_image_url]"]')?.remove();
      form?.querySelector('[name="properties[_preview_image_thumbnail_url]"]')?.remove();
      if (this.legacyPhoto?.url) {
        this.setLineProperty('_preview_image_url', this.legacyPhoto.url, true);
        this.setLineProperty('_preview_image_thumbnail_url', this.legacyPhoto.url, true);
      }
    }

    applyPoster(poster) {
      const full = poster.full?.url || poster.poster_url || '';
      const thumbnail = poster.thumbnail?.url || poster.thumbnail_url || full;
      if (!full) return;
      this.posterFullUrl = full;
      this.posterThumbnailUrl = thumbnail;
      this.setLineProperty('_preview_image_url', full, true);
      this.setLineProperty('_preview_image_thumbnail_url', thumbnail, true);
    }

    showInitial() {
      this.dataset.state = 'initial';
      this.sample.hidden = false;
      this.working.hidden = true;
      this.rendererHost.hidden = true;
      this.statusHeading.textContent = this.dataset.copyInitialHeading;
      this.statusBody.textContent = '';
      this.changePhotoButton.textContent = this.dataset.copyChangePhoto;
      this.changePhotoButton.hidden = true;
      this.retryButton.hidden = true;
    }

    updateSourceImage(url = '') {
      if (!this.sourceImage) return;

      const image = this.sourceImage;
      const sourceUrl = String(url || '');
      const requestId = ++this.sourceImageRequestId;
      image.hidden = true;
      image.onload = null;
      image.onerror = null;

      if (!sourceUrl) {
        image.removeAttribute('src');
        return;
      }

      const revealLoadedImage = () => {
        if (requestId !== this.sourceImageRequestId) return;
        image.hidden = false;
        image.onload = null;
        image.onerror = null;
      };
      const hideFailedImage = () => {
        if (requestId !== this.sourceImageRequestId) return;
        image.hidden = true;
        image.onload = null;
        image.onerror = null;
        image.removeAttribute('src');
      };

      image.onload = revealLoadedImage;
      image.onerror = hideFailedImage;

      if (image.getAttribute('src') !== sourceUrl) image.src = sourceUrl;
      if (image.complete) {
        if (image.naturalWidth > 0) revealLoadedImage();
        else hideFailedImage();
      }
    }

    showWorking(heading, body, phase = 1, generationKind = 'initial') {
      this.dataset.state = 'processing';
      this.generationKind = generationKind;
      this.currentStageIndex = Math.max(1, Math.min(4, Number(phase) || 1));
      this.currentStageLabel = heading;
      if (!this.rendererFrame) this.sample.hidden = false;
      this.working.hidden = false;
      this.rendererHost.hidden = !this.rendererFrame && !this.pendingRendererFrame;
      this.updateSourceImage(this.objectUrl);
      if (this.overlayHeading) {
        this.overlayHeading.textContent = generationKind === 'update'
          ? this.dataset.copyUpdatingHeading
          : this.dataset.copyProcessingHeading;
      }
      if (this.overlayBody) this.overlayBody.textContent = heading;
      if (this.progress) {
        this.progress.dataset.step = String(this.currentStageIndex);
        this.progress.setAttribute('aria-valuenow', String(this.currentStageIndex));
      }
      if (this.progressTime) this.progressTime.textContent = this.dataset.copyProcessingTime;
      this.statusHeading.textContent = heading;
      this.statusBody.textContent = '';
      this.changePhotoButton.textContent = this.dataset.copyChangePhoto;
      this.changePhotoButton.hidden = false;
      this.retryButton.hidden = true;
      const signature = `${heading}|${body}|${this.currentStageIndex}`;
      if (signature !== this.lastWorkingSignature) {
        this.lastWorkingSignature = signature;
        this.emitPreviewState(Date.now() - this.pollStartedAt >= 60000 ? 'slow' : 'creating');
      }
    }

    showError(message, preserveViewer = false) {
      this.dataset.state = 'error';
      this.working.hidden = true;
      if (!preserveViewer) {
        this.sample.hidden = false;
        this.rendererHost.hidden = true;
      }
      this.statusHeading.textContent = this.dataset.copyErrorHeading;
      this.statusBody.textContent = message || this.dataset.copyErrorBody;
      this.changePhotoButton.hidden = true;
      this.retryButton.hidden = false;
      this.emitPreviewState('error');
    }

    handlePreviewFailure(error, options = {}) {
      if (this.failureInProgress) return;
      const phase = options.phase || this.currentStageKey || 'unknown';
      if (this.retryRendererOnce(error, phase, options)) return;
      if (this.rendererAutoRetryInFlight) {
        this.reportClientEvent('viewer_auto_retry_failed', error, {
          phase: 'renderer_retry',
          severity: 'warning',
          retryable: false,
        });
        this.rendererAutoRetryInFlight = false;
      }
      this.failureInProgress = true;
      this.stopPolling();
      window.clearTimeout(this.configureTimer);
      this.configureQueued = false;
      window.clearTimeout(this.rendererSlowTimer);
      this.emitAnalytics('live_preview_v3_failed');
      this.lastFailure = error || new Error(this.dataset.copyErrorBody);
      this.reportClientEvent('preview_failed', this.lastFailure, {
        phase,
        severity: 'error',
        retryable: this.isTransientError(this.lastFailure),
      });
      const reference = this.sessionId ? ` Reference ${this.sessionId.slice(0, 8)}.` : '';
      this.showError(`${this.dataset.copyErrorBody}${reference}`, Boolean(options.preserveViewer));
    }

    retryRendererOnce(error, phase, options = {}) {
      const signature = [
        phase,
        error?.name,
        error?.code,
        error?.message,
      ].filter(Boolean).join(' ').toLowerCase();
      const rendererFailure = phase.includes('renderer')
        || phase.includes('viewer')
        || signature.includes('webgl')
        || signature.includes('context lost')
        || signature.includes('did not settle')
        || signature.includes('iframe failed to load');
      const retryKey = `${this.rendererPath}|${this.currentRevision}`;
      if (
        !rendererFailure
        || !this.rendererPath
        || !this.sessionId
        || retryKey !== this.rendererAutoRetryKey
        || this.rendererAutoRetryAttempts >= MAX_RENDERER_AUTO_RETRIES
      ) return false;

      this.rendererAutoRetryAttempts += 1;
      this.rendererAutoRetryInFlight = true;
      this.lastFailure = error || new Error(this.dataset.copyErrorBody);
      window.clearTimeout(this.rendererSlowTimer);
      window.clearTimeout(this.guidanceTimer);
      this.reportClientEvent('viewer_auto_retry_started', this.lastFailure, {
        phase,
        severity: 'warning',
        retryable: true,
      });

      const preserveViewer = Boolean(options.preserveViewer && this.rendererFrame);
      this.pendingRendererFrame?.remove();
      this.pendingRendererFrame = null;
      this.pendingRendererOrigin = '';
      if (!preserveViewer) {
        this.rendererFrame?.remove();
        this.rendererFrame = null;
        this.expectedRendererOrigin = '';
      }
      this.loadedRendererKey = '';
      this.showWorking(
        this.currentStageLabel || this.dataset.copyProcessingHeading,
        this.dataset.copySlowBody,
        4,
        this.generationKind,
      );
      window.setTimeout(() => {
        if (
          this.v3Active
          && !this.failureInProgress
          && retryKey === this.rendererAutoRetryKey
        ) {
          this.loadRenderer();
        }
      }, RENDERER_AUTO_RETRY_DELAY_MS);
      return true;
    }

    browserDiagnostics() {
      if (!this.browserDiagnosticsCache) {
        let webgl2Available = false;
        let webglContextLost = false;
        let webglVendor = '';
        let webglRenderer = '';
        try {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('webgl2', {
            antialias: false,
            depth: false,
            stencil: false,
          });
          webgl2Available = Boolean(context);
          webglContextLost = Boolean(context?.isContextLost?.());
          const debug = context?.getExtension('WEBGL_debug_renderer_info');
          if (context && debug) {
            webglVendor = String(context.getParameter(debug.UNMASKED_VENDOR_WEBGL) || '').slice(0, 160);
            webglRenderer = String(context.getParameter(debug.UNMASKED_RENDERER_WEBGL) || '').slice(0, 160);
          }
          context?.getExtension('WEBGL_lose_context')?.loseContext();
        } catch (_error) {
          webgl2Available = false;
        }
        this.browserDiagnosticsCache = {
          user_agent: String(navigator.userAgent || '').slice(0, 500),
          browser_language: String(navigator.language || '').slice(0, 40),
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          device_pixel_ratio: Number(window.devicePixelRatio || 1),
          device_memory_gb: Number(navigator.deviceMemory || 0),
          hardware_concurrency: Number(navigator.hardwareConcurrency || 0),
          connection_effective_type: String(navigator.connection?.effectiveType || '').slice(0, 20),
          connection_save_data: Boolean(navigator.connection?.saveData),
          webgl2_available: webgl2Available,
          webgl_context_lost: webglContextLost,
          webgl_vendor: webglVendor,
          webgl_renderer: webglRenderer,
          page_path: window.location.pathname,
        };
      }
      return {
        ...this.browserDiagnosticsCache,
        stage_key: this.currentStageKey || '',
        stage_index: Number(this.currentStageIndex || 0),
        file_bytes: Number(this.file?.size || 0),
        file_media_type: String(this.file?.type || '').slice(0, 100),
        file_width: Number(this.fileMetrics?.width || 0),
        file_height: Number(this.fileMetrics?.height || 0),
      };
    }

    reportClientEvent(eventName, error = null, options = {}) {
      if (!this.clientEventPath || !this.previewToken || !this.sessionId) return Promise.resolve(false);
      const status = Number(error?.status || 0);
      const payload = {
        preview_token: this.previewToken,
        event_name: eventName,
        severity: options.severity || 'error',
        phase: options.phase || this.currentStageKey || 'unknown',
        error_type: String(error?.code || error?.name || '').slice(0, 160),
        message: String(error?.message || '').slice(0, 2000),
        http_status: status >= 100 && status <= 599 ? status : null,
        retryable: Boolean(options.retryable),
        surface: this.dataset.surface || 'pdp',
        release: STOREFRONT_RELEASE,
        diagnostics: {
          ...this.browserDiagnostics(),
          boundary: options.phase || this.currentStageKey || 'unknown',
        },
      };
      return fetch(this.clientEventPath, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then((response) => response.ok).catch(() => false);
    }

    async uploadLegacyPhoto(file, options = {}) {
      const generation = this.photoGeneration || 0;
      const asset = await window.NativePhotoCompat.upload(file);
      if (generation !== (this.photoGeneration || 0)) return null;
      this.nativeAsset = asset;
      return this.rememberLegacyPhoto({url:asset.url,asset_id:asset.asset_id,handle:asset.asset_id,
        filename:file.name,mimetype:asset.media_type,size:asset.bytes},file,options);
    }

    rememberLegacyPhoto(result, file, options = {}) {
      const legacy = {
        url: result.url,
        asset_id: result.asset_id,
        handle: result.handle,
        filename: result.filename || file.name,
        mimetype: result.mimetype || file.type,
        size: result.size || file.size,
        source: result.source || 'local_file_system',
        uploadId: result.uploadId || result.handle,
        accountReorderHydration: options.preserveReorderSource === true,
      };
      this.legacyPhoto = legacy;
      if (!options.preserveReorderSource) this.markAccountReorderPhotoChanged();
      this.setLineProperty('Photo', legacy.url, false);
      if (result.asset_id) this.setLineProperty('_media_asset_id', result.asset_id, true);
      this.setLineProperty('_preview_image_url', this.posterFullUrl || legacy.url, true);
      this.setLineProperty(
        '_preview_image_thumbnail_url',
        this.posterThumbnailUrl || legacy.url,
        true,
      );
      this.emitPreviewState(this.dataset.state === 'ready' ? 'ready' : 'creating');
      this.persistHandoff();
      return legacy;
    }

    markAccountReorderPhotoChanged() {
      const source = this.productForm?.querySelector(
        '[name="properties[_reorder_source_job_id]"]'
      );
      if (!source?.value) return;
      this.setLineProperty('_reorder_photo_changed', 'true', true);
    }

    async requireNativePhotoStorage() {
      return window.NativePhotoCompat.requireNative();
    }

    setLineProperty(name, value, privateProperty) {
      const form = this.productForm || (
        this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null
      );
      if (!form || !value) return;
      const propertyName = `properties[${name}]`;
      let input = form.querySelector(`[name="${propertyName}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = propertyName;
        if (privateProperty) input.dataset.livePreviewV3 = 'true';
        form.append(input);
      }
      input.value = value;
    }

    clearLineProperties() {
      const form = this.productForm;
      if (!form) return;
      [
        'properties[Photo]',
        'properties[_media_asset_id]',
        'properties[_media_order_receipt]',
        'properties[_preview_image_url]',
        'properties[_preview_image_thumbnail_url]',
        'properties[_live_preview_v3_session_id]',
      ].forEach((name) => form.querySelector(`[name="${name}"]`)?.remove());
    }

    async ensureCurrentDesignReady(signal) {
      if (this.createPromise) await this.createPromise;
      const deadline = Date.now() + 120000;

      while (Date.now() < deadline) {
        signal?.throwIfAborted();
        this.syncSelectionFromControls();
        const desiredSignature = this.selectionSignature(this.desiredSelection);
        if (
          desiredSignature !== this.lastSentSignature
          && !this.configureInFlight
          && (this.inferenceStatus === 'ready' || this.statusValue === 'ready' || this.statusValue === 'compiling')
        ) {
          window.clearTimeout(this.configureTimer);
          await this.configure();
        }

        if (
          this.statusValue === 'ready'
          && !this.configureInFlight
          && desiredSignature === this.lastSentSignature
        ) {
          if (this.posterFullUrl && this.posterUploadRevision === this.currentRevision) return;
          this.schedulePosterCapture(0);
        }

        if (this.statusPath && this.previewToken && !this.pollInFlight) {
          const payload = await this.requestJson(this.statusPath, {
            method: 'POST',
            body: JSON.stringify({ preview_token: this.previewToken }),
            diagnosticBoundary: 'cart_submit_status',
            signal,
          });
          this.applyResponse(payload);
        }
        await wait(500);
      }

      throw new Error(this.dataset.copyCartError);
    }

    async handleFormSubmit(event) {
      if (!this.v3Active || this.allowNextSubmit) {
        this.allowNextSubmit = false;
        return;
      }

      if (this.submissionInFlight) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      if (!this.file) {
        if (this.cartEditContext) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showError(this.dataset.copyCartEditError);
        }
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter;
      this.submissionInFlight = true;
      this.setSubmissionUi(true);
      this.productForm?.closest('product-form')?.handleErrorMessage();
      const preparation = new AbortController();
      try {
        await window.StorefrontRequest.run(async (signal) => {
          if (this.nativeMedia) {
            await this.prepareNativeCart(signal);
            return;
          }
          if (!this.sessionId || !this.cartAssociationPath) await this.createPromise;
          if (!this.sessionId || !this.cartAssociationPath || !this.previewToken) {
            throw new Error(this.dataset.copyCartError);
          }

          await this.ensureCurrentDesignReady(signal);
          signal.throwIfAborted();

          const cart = await window.StorefrontRequest.json(`${window.routes?.root || '/'}cart.js`, {
            signal,
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          });
          if (!cart.token) throw new Error(this.dataset.copyCartError);

          if (this.associatedCartToken !== cart.token) {
            const payload = await this.requestJson(this.cartAssociationPath, {
              signal,
              method: 'POST',
              diagnosticBoundary: 'cart_association',
              body: JSON.stringify({
                preview_token: this.previewToken,
                cart_token: cart.token,
              }),
            });
            signal.throwIfAborted();
            this.applyResponse(payload);
            this.associatedCartToken = cart.token;
          }

          signal.throwIfAborted();
        }, { timeoutMs: 150000, signal: preparation.signal });

        if (!this.nativeMedia || this.nativePreviewAssociated) {
          if (this.dataset.state === 'error') this.dataset.state = 'ready';
          this.retryButton.hidden = true;
          this.statusHeading.textContent = this.dataset.copyReadyHeading;
          this.statusBody.textContent = this.dataset.copyReadyBody;
          this.setLineProperty('_live_preview_v3_session_id', this.sessionId, true);
        }
        this.emitAnalytics('live_preview_v3_add_to_cart');
        this.allowNextSubmit = true;
        if (submitter && this.productForm.requestSubmit) this.productForm.requestSubmit(submitter);
        else this.productForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      } catch (error) {
        preparation.abort();
        this.allowNextSubmit = false;
        this.reportClientEvent('cart_prepare_failed', error, {
          phase: 'cart_prepare', severity: 'warning', retryable: true,
        });
        this.releaseSubmissionUi();
        this.showError(this.dataset.copyCartError, true);
      }
    }

    async prepareNativeCart(signal) {
      await this.legacyUploadPromise;
      signal.throwIfAborted();
      if (!this.nativeAsset) throw new Error(this.dataset.copyCartError);
      // GET /cart.js invents a new token on every read before Shopify creates
      // the cart cookie. A no-op update establishes its durable identity.
      const cart = await window.StorefrontRequest.json(`${window.routes?.root || '/'}cart/update.js`, {
        signal, method: 'POST', body: '{}', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });
      if (!cart.token) throw new Error(this.dataset.copyCartError);
      const asset = await window.CustomerMedia.associate(this.nativeAsset, cart.token, signal);
      signal.throwIfAborted();
      if (!asset.order_receipt || asset.asset_id !== this.nativeAsset.asset_id) throw new Error(this.dataset.copyCartError);
      this.setLineProperty('_media_asset_id', asset.asset_id, true);
      if (asset.legacy_archive || this.nativeAsset.legacy_archive) this.setLineProperty('_media_legacy', 'true', true);
      this.setLineProperty('_media_order_receipt', asset.order_receipt, true);
      this.setLineProperty('Photo', asset.url, false);
      this.nativePreviewAssociated = false;
      this.syncSelectionFromControls();
      if (this.statusValue === 'ready' && this.cartAssociationPath && this.previewToken
          && this.selectionSignature(this.desiredSelection) === this.lastSentSignature) {
        try {
          const payload = await this.requestJson(this.cartAssociationPath, {
            signal, method: 'POST', diagnosticBoundary: 'cart_association',
            body: JSON.stringify({ preview_token: this.previewToken, cart_token: cart.token }),
          });
          this.applyResponse(payload);
          this.nativePreviewAssociated = true;
        } catch (_) { signal.throwIfAborted(); }
      }
      if (!this.nativePreviewAssociated) {
        this.productForm?.querySelector('[name="properties[_live_preview_v3_session_id]"]')?.remove();
        this.setLineProperty('_preview_image_url', asset.url, true);
        this.setLineProperty('_preview_image_thumbnail_url', asset.thumbnail_url, true);
      }
    }

    async restoreHistoricalPhoto(sourceUrl = new URL(window.location.href).searchParams.get('sourcefile')) {
      if (!sourceUrl || this.file || !(window.NativePhotoCompat?.legacyUrl(sourceUrl) || window.NativePhotoCompat?.ownedUrl(sourceUrl))) return;
      const selectionId = this.mediaSelectionId || 0;
      const asset = await window.NativePhotoCompat.adopt(sourceUrl);
      if (this.file || selectionId !== (this.mediaSelectionId || 0)) return;
      this.nativeMedia = true; this.nativeAsset = asset; this.file = {restored:true};
      this.objectUrl = asset.url; this.legacyPhoto = asset; this.legacyUploadPromise = Promise.resolve(asset);
      this.setLineProperty('Photo', asset.url, false);
      this.setLineProperty('_media_asset_id', asset.asset_id, true);
      this.setLineProperty('_media_legacy', 'true', true);
      this.syncPhotoPicker();
      this.startPreview(this.file).catch(() => {});
    }

    async restoreNativeHandoff() {
      if (this.file || !window.CustomerMedia) return;
      const record = window.CustomerMedia.handoff();
      if (!record) return;
      const asset = await window.CustomerMedia.verify(record.asset);
      if (this.file || asset.status !== 'ready') return;
      this.nativeMedia = true;
      this.nativeAsset = asset;
      this.file = { restored: true };
      this.objectUrl = asset.url;
      this.legacyPhoto = asset;
      this.legacyUploadPromise = Promise.resolve(asset);
      if (Object.keys(record.selection || {}).length) this.restoreHandoffControls(record.selection);
      this.desiredSelection = this.readSelection();
      this.setLineProperty('Photo', asset.url, false);
      this.setLineProperty('_media_asset_id', asset.asset_id, true);
      this.syncPhotoPicker();
      // Reuse the original S3 upload if the homepage navigated before preview creation.
      this.startPreview(this.file).catch(() => {});
    }

    setSubmissionUi(isLoading) {
      const productFormHost = this.productForm?.closest('product-form');
      const label = this.isCartEditActive() ? 'Updating crystal…' : 'Adding…';
      productFormHost?.setSubmissionLoading?.(isLoading, label, { lock: false });
    }

    releaseSubmissionUi() {
      this.submissionInFlight = false;
      this.productForm?.closest('product-form')?.setSubmissionLoading?.(false);
    }

    async handleHomepageUpload(event) {
      // Choose on the homepage, then create the preview on the product page.
      // This avoids starting a render that navigation would immediately abort.
      const href = event.currentTarget.getAttribute('href');
      event.preventDefault();
      try {
        if (!window.CustomerMedia || !await window.CustomerMedia.enabled()) {
          window.location.assign(href);
          return;
        }
        const choice = await window.CustomerMedia.choose();
        if (!choice) return;
        // A newly chosen homepage photo supersedes a previous product preview.
        try { sessionStorage.removeItem(HANDOFF_KEY); } catch (_) { /* Storage may be unavailable. */ }
        window.CustomerMedia.saveHandoff(choice.asset, {});
        window.location.assign(href);
      } catch (error) {
        this.showError(error?.message || this.dataset.copyErrorBody);
      }
    }

    async handleHomepageNavigation(event) {
      if (!this.v3Active || !this.file) return;
      const href = this.homeProductLink?.getAttribute('href');
      if (!href || href === '#') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        if (this.nativeMedia) {
          await this.legacyUploadPromise;
          if (!this.nativeAsset) throw new Error(this.dataset.copyCartError);
          window.CustomerMedia.saveHandoff(this.nativeAsset, this.desiredSelection);
          this.persistHandoff();
          window.location.assign(href);
          return;
        }
        if (!this.sessionId || !this.previewToken) await this.createPromise;
        if (!this.sessionId || !this.previewToken) {
          throw new Error(this.dataset.copyErrorBody);
        }
        this.persistHandoff();
        window.location.assign(href);
      } catch (error) {
        this.handlePreviewFailure(error, { phase: 'homepage_handoff' });
      }
    }

    syncPhotoPicker() {
      if (this.dataset.surface !== 'pdp') return;
      const sourceUrl = this.legacyPhoto?.url || this.objectUrl;
      if (!sourceUrl) return;
      const scope = this.closest('[id^="MainProduct-"]');
      const image = scope?.querySelector('#uploaded-img');
      if (image) {
        image.src = sourceUrl;
        scope.querySelector('#uploaded-image')?.classList.remove('hide');
      }
      const button = scope?.querySelector('#product-file-picker');
      if (button) button.textContent = 'Change photo';
    }

    emitPreviewState(state) {
      // V3 intercepts the legacy picker, so its upload callback no longer
      // updates the photo thumbnail and button next to the product options.
      this.syncPhotoPicker();
      const detail = {
        formId: this.dataset.productFormId,
        state,
        imageUrl: this.posterFullUrl || this.legacyPhoto?.url || this.objectUrl,
        thumbnailUrl: this.posterThumbnailUrl || this.legacyPhoto?.url || this.objectUrl,
        provider: 'v3',
        stageKey: this.currentStageKey,
        stageIndex: this.currentStageIndex,
        stageCount: 4,
        stageLabel: this.currentStageLabel,
        generationKind: this.generationKind,
      };
      const signature = JSON.stringify([
        state,
        detail.imageUrl,
        detail.thumbnailUrl,
        detail.stageKey,
        detail.stageIndex,
        detail.stageLabel,
        detail.generationKind,
      ]);
      if (signature === this.lastPreviewStateSignature) return;
      this.lastPreviewStateSignature = signature;
      document.dispatchEvent(new CustomEvent('ccm:preview-state', {
        detail,
      }));
    }

    emitAnalytics(eventName) {
      const lifecycleEvents = new Set([
        'live_preview_v3_upload_started',
        'live_preview_v3_session_created',
        'live_preview_v3_ready',
        'live_preview_v3_failed',
      ]);
      if (lifecycleEvents.has(eventName)) {
        if (this.analyticsEmitted.has(eventName)) return;
        this.analyticsEmitted.add(eventName);
      }
      const selection = this.desiredSelection || {};
      const variantId = this.productForm?.querySelector('[name="id"]')?.value || '';
      const payload = {
        event: eventName,
        live_preview_store: this.dataset.store || 'dccom',
        live_preview_version: 'v3',
        storefront_epoch: 'dccom-live-preview-v3-20260815',
        variant_id: variantId,
        live_preview_crystal_sku: selection.crystal_sku || '',
        live_preview_base_sku: selection.base_sku || 'none',
        live_preview_text_present: Boolean(selection.engraving_text),
        live_preview_wall_time_ms: this.pollStartedAt ? Date.now() - this.pollStartedAt : 0,
        live_preview_session_id: this.sessionId || '',
      };
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
      const crystalPricingEvent = {
        live_preview_v3_upload_started: 'crystal_preview_started',
        live_preview_v3_ready: 'crystal_preview_completed',
        live_preview_v3_failed: 'crystal_preview_failed',
      }[eventName];
      if (crystalPricingEvent && typeof window.Shopify?.analytics?.publish === 'function') {
        window.Shopify.analytics.publish(crystalPricingEvent, {
          previewVersion: 'v3',
          storefrontEpoch: payload.storefront_epoch,
          variantId,
          crystalSku: payload.live_preview_crystal_sku,
          baseSku: payload.live_preview_base_sku,
          textPresent: payload.live_preview_text_present,
          wallTimeMs: payload.live_preview_wall_time_ms,
        });
      }
      this.dispatchEvent(new CustomEvent(eventName, { detail: payload, bubbles: true }));
    }

    persistHandoff() {
      if (!this.sessionId || !this.previewToken) return;
      if (this.dataset.surface === 'pdp' && this.productForm) this.syncSelectionFromControls();
      const productScope = this.closest('product-info') || document;
      const addOnVariantIds = Array.from(new Set([
        ...Array.from(
          this.productForm?.querySelectorAll('input[data-addons-index][name$="[id]"]') || []
        ).map((input) => String(input.value || '')),
        ...Array.from(productScope.querySelectorAll('.product-additional__wrapper:not(.hide)'))
          .map((wrapper) => String(wrapper.querySelector('.product-form__buttons[data-id]')?.dataset.id || '')),
        String(productScope.querySelector('.product-option__guarant input[type="radio"]:checked')?.value || ''),
      ].filter(Boolean)));
      const record = {
        version: 1,
        storefrontRelease: STOREFRONT_RELEASE,
        savedAt: Date.now(),
        sessionId: this.sessionId,
        previewToken: this.previewToken,
        statusPath: this.statusPath,
        configurePath: this.configurePath,
        cartAssociationPath: this.cartAssociationPath,
        rendererPath: this.rendererPath,
        posterPath: this.posterPath,
        clientEventPath: this.clientEventPath,
        appearanceVersion: APPEARANCE_VERSION,
        posterFullUrl: this.posterFullUrl,
        posterThumbnailUrl: this.posterThumbnailUrl,
        status: this.statusValue,
        inferenceStatus: this.inferenceStatus,
        compilationRevision: this.currentRevision,
        configuredSelectionSignature: this.lastSentSignature || '',
        quantity: Number(productScope.querySelector('.quantity__input')?.value || 1),
        selection: {
          crystal_sku: this.desiredSelection.crystal_sku,
          orientation: this.desiredSelection.orientation,
          base_sku: this.desiredSelection.base_sku,
          engraving_text: this.desiredSelection.engraving_text,
          engraving_font: this.desiredSelection.engraving_font,
        },
        legacyPhoto: this.legacyPhoto,
        keepBackground: Boolean(this.keepBackgroundControl()?.checked),
        addOnVariantIds,
        dccomAddOnStateCaptured: true,
        cartEditOriginalSessionId: this.cartEditContext?.sessionId || '',
        cartEditOriginalGroupId: this.cartEditContext?.groupId || '',
        cartEditOriginalLineKeys: Array.isArray(this.cartEditContext?.originalKeys)
          ? this.cartEditContext.originalKeys
          : [],
        cartEditProofRequested: Boolean(document.querySelector(
          `[data-proof-preview-toggle][data-proof-preview-form="${CSS.escape(this.dataset.productFormId || '')}"]`
        )?.checked),
        cartEditOrientationAllowed: Boolean(document.querySelector(
          `[name="properties[Orientation]"][form="${CSS.escape(this.dataset.productFormId || '')}"]`
        )?.checked),
      };
      try {
        sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(record));
      } catch (_error) {
        // The same-page preview remains usable when session storage is blocked.
      }
    }

    cartEditSessionId() {
      const value = new URLSearchParams(window.location.search).get('edit_preview_session') || '';
      return /^[a-f0-9-]{36}$/i.test(value) ? value.toLowerCase() : '';
    }

    cartEditTarget() {
      const parameters = new URLSearchParams(window.location.search);
      return {
        groupId: parameters.get('edit_cart_group') || '',
        lineKey: parameters.get('edit_cart_key') || '',
      };
    }

    cartItemProperties(item) {
      return item?.properties && typeof item.properties === 'object' ? item.properties : {};
    }

    cartGroupId(properties = {}) {
      return properties['__Group Identify'] || properties['_Group Identify'] || '';
    }

    cartEngravingSelection(properties = {}, fallback = {}) {
      const rawText = String(
        properties['Engrave Text']
        || properties['Engrave text']
        || fallback.engraving_text
        || ''
      );
      const suffix = rawText.match(/\s+\((Georgia|Monotype Corsiva)\)$/);
      return {
        engraving_text: rawText.replace(/\s+\((?:Georgia|Monotype Corsiva)\)$/, ''),
        engraving_font: properties.Font
          || suffix?.[1]
          || fallback.engraving_font
          || 'Georgia',
      };
    }

    restoreDccomGroupedOptions(groupItems = [], selection = {}, savedAddOnVariantIds = null) {
      const scope = this.closest('product-info') || document;
      const groupedByVariant = new Map(
        groupItems.map((item) => [String(item.variant_id || item.id || ''), item])
      );
      const selectedAddOnIds = Array.isArray(savedAddOnVariantIds)
        ? new Set(savedAddOnVariantIds.map(String))
        : new Set(groupedByVariant.keys());
      const targetBaseSku = String(selection.base_sku || 'none');
      const baseInputs = Array.from(scope.querySelectorAll('input[name="base"][data-base-sku]'));
      const baseInput = baseInputs.find((input) => {
        const candidateSku = input.dataset.baseSku === 'auto-black'
          ? blackBaseSkuForCrystal(
            selection.crystal_sku || this.desiredSelection?.crystal_sku || this.dataset.initialSku,
            selection.orientation || this.dataset.defaultOrientation,
          )
          : input.dataset.baseSku || 'none';
        return String(candidateSku) === targetBaseSku;
      }) || baseInputs.find((input) => groupedByVariant.has(String(input.value || '')));
      if (baseInput && !baseInput.checked) baseInput.click();

      const guaranteeInputs = Array.from(
        scope.querySelectorAll('.product-option__guarant input[type="radio"]')
      );
      const guaranteeInput = guaranteeInputs.find((input) => selectedAddOnIds.has(String(input.value || '')));
      guaranteeInputs.forEach((input) => {
        const shouldCheck = input === guaranteeInput;
        if (input.checked !== shouldCheck) input.checked = shouldCheck;
      });

      scope.querySelectorAll('.product-additional__wrapper').forEach((wrapper) => {
        const id = String(wrapper.querySelector('.product-form__buttons[data-id]')?.dataset.id || '');
        const groupedItem = groupedByVariant.get(id);
        const quantity = selectedAddOnIds.has(id) ? Number(groupedItem?.quantity || 1) : 0;
        wrapper.classList.toggle('hide', quantity <= 0);
        const quantityInput = wrapper.querySelector('.product-form__quant input[type="number"]');
        if (quantityInput) quantityInput.value = String(quantity);
        const hiddenQuantity = this.productForm?.querySelector(`input.product-additional[data-id="${CSS.escape(id)}"]`);
        if (hiddenQuantity) hiddenQuantity.value = String(quantity);
      });

      document.dispatchEvent(new Event('change', { bubbles: true }));
    }

    restoreDccomCartControls(
      selection,
      properties = {},
      groupItems = [],
      keepBackground = false,
      savedAddOnVariantIds = null,
    ) {
      const scope = this.closest('product-info') || document;
      this.restoreHandoffControls(selection);

      const visibleText = scope.querySelector('.engrave-text__input input[type="text"]');
      if (visibleText) {
        visibleText.value = selection.engraving_text || '';
        visibleText.dispatchEvent(new Event('input', { bubbles: true }));
      }
      const visibleFont = Array.from(
        scope.querySelectorAll('.engrave-text__styles input[name="font_style"]')
      ).find((input) => input.value === (selection.engraving_font || 'Georgia'));
      if (visibleFont) {
        if (!visibleFont.checked) visibleFont.click();
        else visibleFont.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.syncBackgroundChoice(Boolean(keepBackground));
      this.keepBackgroundControl()?.dispatchEvent(new Event('change', { bubbles: true }));
      this.restoreDccomGroupedOptions(groupItems, selection, savedAddOnVariantIds);
      this.desiredSelection = { ...this.desiredSelection, ...this.readSelection(), ...selection };
      return this.desiredSelection;
    }

    async waitForCartEditControls() {
      if (document.readyState === 'loading') {
        await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
      }
      await wait(350);
    }

    setCartEditControlValue(name, value) {
      const selector = `[name="${CSS.escape(name)}"]`;
      const controls = Array.from(document.querySelectorAll(selector)).filter((control) => {
        const formId = control.getAttribute('form');
        return !formId || formId === this.dataset.productFormId || this.productForm?.contains(control);
      });
      controls.forEach((control) => {
        if (!('value' in control)) return;
        control.value = value == null ? '' : String(value);
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    hydrateCartEditControls(parentItem, groupItems, handoffRecord = null) {
      const properties = this.cartItemProperties(parentItem);
      if (!handoffRecord) {
        Object.entries(properties).forEach(([property, value]) => {
          if (!property.startsWith('_') && property !== 'Photo' && property !== 'Crystal SKU') {
            this.setCartEditControlValue(`properties[${property}]`, value);
          }
        });
      } else {
        this.setCartEditControlValue(
          'properties[Engrave Text]',
          handoffRecord.selection?.engraving_text || '',
        );
        this.setCartEditControlValue(
          'properties[Engrave text]',
          handoffRecord.selection?.engraving_text || '',
        );
        this.setCartEditControlValue(
          'properties[Font]',
          handoffRecord.selection?.engraving_font || 'Georgia',
        );
      }

      const quantity = this.productForm?.querySelector('[name="quantity"]');
      if (quantity && parentItem.quantity) quantity.value = String(parentItem.quantity);

      const proofToggle = document.querySelector(
        `[data-proof-preview-toggle][data-proof-preview-form="${CSS.escape(this.dataset.productFormId)}"]`
      );
      if (proofToggle) {
        proofToggle.checked = handoffRecord
          ? Boolean(handoffRecord.cartEditProofRequested)
          : properties['Request 3D Proof'] === 'Yes' || properties._customer_proof_requested === '1';
      }

      const orientationToggle = document.querySelector(
        `[name="properties[Orientation]"][form="${CSS.escape(this.dataset.productFormId)}"]`
      );
      if (orientationToggle) {
        orientationToggle.checked = handoffRecord
          && typeof handoffRecord.cartEditOrientationAllowed === 'boolean'
          ? handoffRecord.cartEditOrientationAllowed
          : Boolean(properties.Orientation);
        orientationToggle.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const groupedVariantIds = new Set(groupItems.map((item) => String(item.variant_id || item.id || '')));
      const scope = this.closest('product-info') || document;
      const baseInput = Array.from(scope.querySelectorAll(
        'input[name="base"][data-base-sku], variant-selects-extended input[data-variant-id]'
      ))
        .find((input) => (
          handoffRecord?.selection?.base_sku
            ? String(input.dataset.baseSku || '') === String(handoffRecord.selection.base_sku)
            : groupedVariantIds.has(String(input.dataset.variantId || input.value || ''))
        ));
      if (baseInput && !baseInput.checked) baseInput.click();

      const addOnVariantIds = handoffRecord
        ? new Set((handoffRecord.addOnVariantIds || []).map(String))
        : groupedVariantIds;
      scope.querySelectorAll('.additional-row button[data-variant-id]').forEach((button) => {
        const variantId = String(button.dataset.variantId || '');
        if (!variantId || !addOnVariantIds.has(variantId)) return;
        const alreadySelected = this.productForm?.querySelector(
          `input[data-addons-index][value="${CSS.escape(variantId)}"]`
        );
        if (!alreadySelected) button.click();
      });

      this.productForm?.closest('product-form')?.syncCustomerProofPreviewProperties?.();
      this.desiredSelection = this.readSelection();
    }

    setSubmitButtonLabel(button, label) {
      const directSpan = button.querySelector(':scope > span');
      if (directSpan) {
        directSpan.textContent = label;
        return;
      }
      const textNode = Array.from(button.childNodes)
        .find((node) => node.nodeType === Node.TEXT_NODE && node.nodeValue.trim());
      if (textNode) {
        textNode.nodeValue = `\n      ${label}\n    `;
        return;
      }
      button.prepend(document.createTextNode(label));
    }

    activateCartEditMode() {
      const productInfo = this.closest('product-info');
      productInfo?.classList.add('is-v3-cart-edit');
      if (this.cartEditBanner) this.cartEditBanner.hidden = false;

      const formId = this.dataset.productFormId;
      document.querySelectorAll(
        `#${CSS.escape(formId)} button[type="submit"], button[type="submit"][form="${CSS.escape(formId)}"]`
      ).forEach((button) => this.setSubmitButtonLabel(button, 'Update crystal'));

      const productFormHost = this.productForm?.closest('product-form');
      if (productFormHost) productFormHost.cartEditCoordinator = this;
    }

    async restoreCartEdit(sessionId) {
      this.restoringCartEdit = true;
      try {
        const editTarget = this.cartEditTarget();
        this.cartEditContext = { sessionId, groupId: editTarget.groupId, originalKeys: [] };
        this.activateCartEditMode();
        this.currentStageKey = 'intake';
        this.currentStageIndex = 1;
        this.currentStageLabel = this.stageLabels.intake || this.dataset.copyProcessingHeading;
        this.pollStartedAt = Date.now();
        this.showWorking(
          this.dataset.copyUpdatingHeading,
          this.dataset.copyProcessingBody,
          1,
          'update',
        );

        const cart = await window.StorefrontRequest.json(`${window.routes?.root || '/'}cart.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        const parentItem = cart.items?.find((item) => {
          const properties = this.cartItemProperties(item);
          if (properties._live_preview_v3_session_id !== sessionId) return false;
          if (editTarget.groupId && this.cartGroupId(properties) !== editTarget.groupId) return false;
          if (editTarget.lineKey && item.key !== editTarget.lineKey) return false;
          return true;
        });
        if (!parentItem || !cart.token) throw new Error(this.dataset.copyCartEditError);

        const parentProperties = this.cartItemProperties(parentItem);
        if (parentProperties._media_asset_id) {
          const native = await window.CustomerMedia.verify({ asset_id: parentProperties._media_asset_id });
          if (native.status !== 'ready') throw new Error(this.dataset.copyCartEditError);
          this.nativeMedia = true;
          this.nativeAsset = native;
        }
        const groupId = this.cartGroupId(parentProperties);
        const groupItems = groupId
          ? cart.items.filter((item) => this.cartGroupId(this.cartItemProperties(item)) === groupId)
          : [parentItem];
        const groupedV3Parents = groupItems.filter((item) => (
          Boolean(this.cartItemProperties(item)._live_preview_v3_session_id)
        ));
        if (groupedV3Parents.length > 1) {
          throw new Error('These customized items are combined in one cart group. Remove the extra copy before editing this crystal.');
        }

        const editHandoff = this.readHandoffRecord();
        const validEditHandoff = this.validHandoffRecord(editHandoff);
        const handoffMatchesActiveSession = validEditHandoff && editHandoff.sessionId === sessionId;
        const handoffMatchesCartSource = validEditHandoff
          && editHandoff.cartEditOriginalSessionId === sessionId
          && (!editTarget.groupId || editHandoff.cartEditOriginalGroupId === editTarget.groupId)
          && (
            !editTarget.lineKey
            || editHandoff.cartEditOriginalLineKeys?.includes(editTarget.lineKey)
          );
        const canUseEditHandoff = handoffMatchesActiveSession || handoffMatchesCartSource;

        await this.waitForCartEditControls();
        this.hydrateCartEditControls(
          parentItem,
          groupItems,
          canUseEditHandoff ? editHandoff : null,
        );

        if (canUseEditHandoff) {
          this.applyHandoffRecord(editHandoff);
          this.restoreDccomCartControls(
            editHandoff.selection || {},
            parentProperties,
            groupItems,
            Boolean(editHandoff.keepBackground),
            editHandoff.dccomAddOnStateCaptured ? (editHandoff.addOnVariantIds || []) : null,
          );
          this.associatedCartToken = editHandoff.sessionId === sessionId ? cart.token : '';
        } else {
          const createUrl = new URL(this.dataset.createPath, window.location.origin);
          const resumePath = `${createUrl.pathname.replace(/\/$/, '')}/${encodeURIComponent(sessionId)}/resume?preview_v3=1`;
          const payload = await this.requestJson(resumePath, {
            method: 'POST',
            body: JSON.stringify({ cart_token: cart.token }),
            diagnosticBoundary: 'cart_resume',
          });

          this.sessionId = sessionId;
          this.file = { restored: true };
          this.legacyPhoto = parentProperties.Photo
            ? { ...(this.nativeAsset || {}), url: parentProperties.Photo, filename: 'customer-photo' }
            : null;
          this.legacyUploadPromise = Promise.resolve(this.legacyPhoto);
          this.objectUrl = this.legacyPhoto?.url || parentProperties._preview_image_thumbnail_url || '';
          const engravingSelection = this.cartEngravingSelection(parentProperties, this.desiredSelection);
          const restoredSelection = {
            ...this.desiredSelection,
            crystal_sku: payload.crystal_sku
              || parentProperties['Crystal SKU']
              || this.desiredSelection.crystal_sku
              || this.dataset.initialSku
              || '',
            base_sku: payload.base_sku || this.desiredSelection.base_sku,
            ...engravingSelection,
          };
          this.applyResponse(payload);
          const removeBackground = String(parentProperties['Remove Background'] || '').toLowerCase();
          const keepBackground = removeBackground
            ? ['no', 'false', '0'].includes(removeBackground)
            : false;
          this.restoreDccomCartControls(
            restoredSelection,
            parentProperties,
            groupItems,
            keepBackground,
          );
          this.lastSentSignature = this.selectionSignature(this.desiredSelection);
          this.associatedCartToken = cart.token;
        }
        this.cartEditContext = {
          sessionId,
          groupId,
          originalKeys: groupItems.map((item) => item.key).filter(Boolean),
        };

        if (!this.legacyPhoto && parentProperties.Photo) {
          this.legacyPhoto = { ...(this.nativeAsset || {}), url: parentProperties.Photo, filename: 'customer-photo' };
          this.legacyUploadPromise = Promise.resolve(this.legacyPhoto);
        }
        if (this.legacyPhoto?.url) this.setLineProperty('Photo', this.legacyPhoto.url, false);
        this.setLineProperty('_live_preview_v3_session_id', this.sessionId, true);
        this.activateCartEditMode();
        this.persistHandoff();

        if (new URLSearchParams(window.location.search).get('edit_focus') === 'text') {
          const textControl = (this.closest('product-info') || document)
            .querySelector('.engrave-text__input input[type="text"]')
            || this.productForm?.querySelector('[name="properties[Engrave text]"]');
          textControl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          window.setTimeout(() => textControl?.focus({ preventScroll: true }), 450);
        }
      } catch (error) {
        this.showError(error?.message || this.dataset.copyCartEditError);
        throw error;
      } finally {
        this.restoringCartEdit = false;
      }
    }

    isCartEditActive() {
      return Boolean(this.cartEditContext?.sessionId);
    }

    async completeCartReplacement(addResponse) {
      if (!this.isCartEditActive()) return false;
      const addedItems = Array.isArray(addResponse?.items) ? addResponse.items : [addResponse];
      const replacementParent = addedItems.find((item) => (
        this.nativeMedia
          ? this.cartItemProperties(item)._media_asset_id === this.nativeAsset?.asset_id
          : this.cartItemProperties(item)._live_preview_v3_session_id === this.sessionId
      ));
      if (!replacementParent) {
        throw new Error('The updated crystal was not added. Your original cart item is unchanged.');
      }

      const replacementGroupId = this.cartGroupId(this.cartItemProperties(replacementParent));
      if (replacementGroupId && replacementGroupId === this.cartEditContext.groupId) {
        throw new Error('The updated crystal could not be separated from the original cart item.');
      }

      const cart = await window.StorefrontRequest.json(`${window.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      const replacementExists = cart.items?.some((item) => {
        const properties = this.cartItemProperties(item);
        return (this.nativeMedia
          ? properties._media_asset_id === this.nativeAsset?.asset_id
          : properties._live_preview_v3_session_id === this.sessionId)
          && (!replacementGroupId || this.cartGroupId(properties) === replacementGroupId);
      });
      if (!replacementExists) {
        throw new Error('The updated crystal could not be verified. Your original cart item is unchanged.');
      }

      const originalKeys = new Set(this.cartEditContext.originalKeys || []);
      const originals = cart.items.filter((item) => {
        const properties = this.cartItemProperties(item);
        if (replacementGroupId && this.cartGroupId(properties) === replacementGroupId) return false;
        if (this.cartEditContext.groupId) {
          return this.cartGroupId(properties) === this.cartEditContext.groupId;
        }
        return originalKeys.has(item.key);
      });
      if (!originals.length) {
        throw new Error('Your updated crystal was added, but the original cart item could not be found.');
      }

      const updates = Object.fromEntries(originals.map((item) => [item.key, 0]));
      const updatedCart = await window.StorefrontRequest.json(`${window.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!updatedCart.items) {
        throw new Error('Your updated crystal was added, but the original could not be removed.');
      }

      try {
        sessionStorage.removeItem(HANDOFF_KEY);
      } catch (_error) {
        // Navigation still completes when session storage is unavailable.
      }
      window.location.assign(`${window.routes?.root || '/'}cart`);
      return true;
    }

    readHandoffRecord() {
      try {
        return safeJsonParse(sessionStorage.getItem(HANDOFF_KEY));
      } catch (_error) {
        return null;
      }
    }

    validHandoffRecord(record) {
      return Boolean(
        record
        && record.version === 1
        && record.sessionId
        && record.previewToken
        && Date.now() - Number(record.savedAt || 0) <= HANDOFF_MAX_AGE_MS
      );
    }

    restoreHandoff() {
      const record = this.readHandoffRecord();
      if (!this.validHandoffRecord(record)) return;
      this.applyHandoffRecord(record);
    }

    restoreHandoffControls(selection = {}) {
      if (this.dataset.surface !== 'pdp') return;
      const scope = this.closest('product-info') || document;
      const crystalInput = Array.from(scope.querySelectorAll('.product-option__size input[data-crystal-sku]'))
        .find(input => input.dataset.crystalSku === selection.crystal_sku);
      if (crystalInput) {
        crystalInput.checked = true;
        const variant = this.productForm?.querySelector('.product-variant-id');
        if (variant) {
          variant.value = crystalInput.dataset.variantId;
          variant.dataset.sku = crystalInput.dataset.crystalSku;
        }
        // Update the price header and theme's variant model through its normal path.
        crystalInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const baseSku = String(selection.base_sku || 'none');
      const baseInput = Array.from(
        scope.querySelectorAll('input[name="base"][data-base-sku], variant-selects-extended input[data-base-sku]')
      ).find((input) => {
        const candidateSku = input.dataset.baseSku === 'auto-black'
          ? blackBaseSkuForCrystal(
            selection.crystal_sku || this.desiredSelection?.crystal_sku || this.dataset.initialSku,
            selection.orientation || this.dataset.defaultOrientation,
          )
          : input.dataset.baseSku || 'none';
        return String(candidateSku) === baseSku;
      });
      if (baseInput && !baseInput.checked) {
        baseInput.checked = true;
        baseInput.closest('variant-selects-extended')?.updateProductForm?.();
      }

      const setControlValue = (name, value) => {
        const formId = this.dataset.productFormId;
        const control = formId
          ? document.querySelector(
            `[form="${CSS.escape(formId)}"][name="${CSS.escape(name)}"]`
          ) || this.productForm?.querySelector(`[name="${CSS.escape(name)}"]`)
          : this.productForm?.querySelector(`[name="${CSS.escape(name)}"]`);
        if (control && 'value' in control) control.value = value == null ? '' : String(value);
      };
      setControlValue('properties[Engrave Text]', selection.engraving_text || '');
      setControlValue('properties[Engrave text]', selection.engraving_text || '');
      setControlValue('properties[Font]', selection.engraving_font || 'Georgia');

      const visibleText = scope.querySelector('.engrave-text__input input[type="text"]');
      if (visibleText) visibleText.value = selection.engraving_text || '';
      const visibleFont = Array.from(
        scope.querySelectorAll('.engrave-text__styles input[name="font_style"]')
      ).find((input) => input.value === (selection.engraving_font || 'Georgia'));
      if (visibleFont) visibleFont.checked = true;
    }

    applyHandoffRecord(record) {
      this.sessionId = record.sessionId;
      this.previewToken = record.previewToken;
      this.statusPath = record.statusPath || '';
      this.configurePath = record.configurePath || '';
      this.cartAssociationPath = record.cartAssociationPath || '';
      // A saved session remains reusable across storefront releases, but its
      // renderer URL is release-specific. Leaving this blank forces the
      // immediate status refresh below to return the current versioned path
      // instead of briefly mounting a renderer from an older release.
      this.rendererPath = '';
      this.posterPath = record.posterPath || '';
      this.clientEventPath = record.clientEventPath || '';
      this.posterFullUrl = record.appearanceVersion === APPEARANCE_VERSION ? record.posterFullUrl || '' : '';
      this.posterThumbnailUrl = record.appearanceVersion === APPEARANCE_VERSION ? record.posterThumbnailUrl || '' : '';
      this.statusValue = record.status || '';
      this.inferenceStatus = record.inferenceStatus || '';
      this.currentRevision = Number(record.compilationRevision ?? -1);
      this.posterUploadRevision = this.posterFullUrl && this.currentRevision >= 0
        ? this.currentRevision
        : -1;
      this.posterUploadingRevision = -1;
      this.legacyPhoto = record.legacyPhoto || null;
      this.nativeMedia = Boolean(this.legacyPhoto?.asset_id);
      this.nativeAsset = this.nativeMedia ? this.legacyPhoto : null;
      this.syncBackgroundChoice(Boolean(record.keepBackground));
      this.file = { restored: true };
      this.legacyUploadPromise = Promise.resolve(this.legacyPhoto);
      if (this.legacyPhoto?.url) {
        this.objectUrl = this.legacyPhoto.url;
        this.setLineProperty('Photo', this.legacyPhoto.url, false);
        this.setLineProperty(
          '_preview_image_url',
          this.posterFullUrl || this.legacyPhoto.url,
          true,
        );
        this.setLineProperty(
          '_preview_image_thumbnail_url',
          this.posterThumbnailUrl || this.legacyPhoto.url,
          true,
        );
      }

      this.restoreHandoffControls(record.selection || {});
      if (this.dataset.surface === 'pdp') {
        const scope = this.closest('product-info') || document;
        const quantity = scope.querySelector('.quantity__input');
        if (quantity && Number.isInteger(record.quantity) && record.quantity > 0) quantity.value = String(record.quantity);
        if (record.dccomAddOnStateCaptured) {
          this.restoreDccomGroupedOptions([], record.selection || {}, record.addOnVariantIds || []);
          const proof = scope.querySelector('[data-proof-preview-toggle]');
          if (proof) {
            proof.checked = Boolean(record.cartEditProofRequested);
            proof.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        document.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const currentSelection = this.readSelection();
      const restoredSelection = {
        ...this.desiredSelection,
        ...(record.selection || {}),
        ...currentSelection,
      };
      this.desiredSelection = restoredSelection;
      // This signature describes the server render, not the newly restored UI.
      // A different/current product must be configured before its old render appears.
      this.lastSentSignature = record.configuredSelectionSignature || '';
      // Older records did not track the configured render separately. Reconfirm
      // them once, including sessions already affected by the refresh mismatch.
      this.catalogViewer?.setSku?.(restoredSelection.crystal_sku);
      this.catalogViewer?.setAttribute('base-mode', restoredSelection.base_sku || 'none');
      const currentSelectionSignature = this.selectionSignature(currentSelection);
      this.generationKind = 'initial';
      this.currentStageIndex = 1;
      this.currentStageKey = 'intake';
      this.currentStageLabel = this.stageLabels.intake || this.dataset.copyProcessingHeading;
      this.showWorking(
        this.currentStageLabel,
        this.dataset.copyProcessingBody,
        1,
        this.generationKind,
      );
      this.emitPreviewState('creating');
      this.pollStartedAt = Date.now();
      if (currentSelectionSignature !== this.lastSentSignature) this.scheduleConfigure(0);
      if (this.statusPath) {
        this.statusValue = '';
        this.schedulePoll();
      }
    }

    focusViewer() {
      this.setAttribute('tabindex', '-1');
      this.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
      this.focus({ preventScroll: true });
    }
  }

  customElements.define('ccm-live-preview-v3', CcmLivePreviewV3);
})();
